import React, {
  Suspense, lazy, useCallback, useEffect, useRef, useState,
} from 'react';
import './App.css';
import { initAudio, playSound, setMuted, isMuted } from './sound';
import { playMusic, stopMusic, preloadMusic } from './music';
import { getRecord, recordWin, resetRecord } from './score';
import {
  getBalance, placeStake, settleSeries, winningsFor, topUp,
  canCover, MIN_STAKE, MAX_STAKE, TOP_UP_CEILING,
} from './wallet';
import { getAIInput, DIFFICULTIES } from './ai';
import { installPlaytestBridge, playtestMode, playtestRequested } from './playtest';
import Fold, { useRoomyScreen } from './Fold';
import { readGamepad, padForSlot, padCount } from './gamepad';
import {
  screenWidth,
  SCREEN_WIDTHS,
  START_SCREEN,
  SCREEN_COUNT,
  CANVAS_HEIGHT,
  BOARD_SPEED,
  BOARD_CLIMB_SPEED,
  PLAYER_SPEED,
  BEAM_RECHARGE_TIME,
  STEP_MS,
  MAX_CATCHUP_STEPS,
  GROUND_Y,
  CEILING_Y,
  BODY_TOP,
  BOARD_HALF_WIDTH,
  BEAM_CHARGE_TIME,
  DRAW_TIME,
  STOW_TIME,
  MOUNT_TIME,
  VAPORIZE_TIME,
  HORIZON_Y,
  DECK_SEAM_SPACING,
  CELL_DOOR_TOP_Y,
  CELL_DOOR_BOTTOM_Y,
  fitsThroughCellDoor,
  SCREEN_TRANSITION_TIME,
  CLUSTER_MAX,
  CLUSTER_RECHARGE,
  EMP_MAX,
  EMP_RECHARGE,
  EMP_SWEEP_TIME,
  CLUSTER_BLAST_RADIUS,
  EMP_BOARD_DOWN_TIME,
  BLAST_LIFE,
} from './constants';
import Lobby from './Lobby';
import PauseMenu from './PauseMenu';
import EndScreen from './EndScreen';
import { paintTerritoryTint } from './backgrounds';
import {
  preloadSprites,
  getSprite,
  getSilhouette,
  getTinted,
  GEOMETRY,
  SPRITE_BOTTOM,
  DECK_INSET,
  CYCLES,
  HELD_BOARD,
  RECOIL_KICK,
} from './sprites';
import { getObstacles } from './obstacles';
import {
  preloadParallax, drawParallax, drawRooftop, drawDeckReflection, BLEED_SOURCE,
} from './parallax';
import {
  computeView, viewChanged, stageBox, stageChanged, ARENA_MARGIN,
} from './viewport';
import {
  createCamera, cameraTarget, advanceCamera, worldToFrame, frameToWorld,
} from './camera';
import { steerToPointer } from './steering';
import { isTouchPrimary } from './touch';
import { localMultiplayerEnabled } from './features';

// The phone's match header, split into its own chunk: only an upright
// touchscreen ever shows it, and its portraits are small enough that the
// bundler inlines them — so bundled eagerly they were ~19KB every desktop
// player downloaded for nothing.
const MobileHud = lazy(() => import('./MobileHud'));
// The thumb console, likewise: its painted frame and icons are for
// touchscreens only.
const TouchControls = lazy(() => import('./TouchControls'));
/**
 * A lazy component that renders on its FIRST pass once its module is loaded.
 *
 * React.lazy suspends for one render even when the import finished long ago,
 * because import() always settles asynchronously. For most chunks that is an
 * invisible frame. For the controls panel it is a lost keypress: its "any key
 * starts the fight" listener is not mounted yet, the key goes nowhere, and the
 * match then waits behind a panel the player already dismissed. React's lazy
 * accepts any thenable and treats one that resolves synchronously as loaded,
 * so once the module is in, it is handed one.
 *
 * @param {Function} factory () => import('./Module')
 * @returns {[object, Function]} the component, and a preload function.
 */
function preloadableLazy(factory) {
  let loaded = null;
  const preload = () => factory().then((m) => { loaded = m; return m; });
  const Component = lazy(() => (
    loaded ? { then: (resolve) => resolve(loaded) } : preload()
  ));
  return [Component, preload];
}

// The controls panel carries a painted frame, two illustrations and the
// painted icons, none of which the menu needs — its own chunk, fetched as soon
// as the app starts (see the preload effect) so it renders, "any key" listener
// and all, the moment a match is started.
const [ControlsScreen, preloadControlsScreen] = preloadableLazy(
  () => import('./ControlsScreen')
);
import {
  MOVES,
  MELEE_MAX_HP,
  canFight,
  isDiving,
  diveAngle,
  meleePose,
} from './melee';
import {
  drawHoloBands,
  drawHoloScan,
  holoAlpha,
  chromaSplit,
  HOLO_CYAN,
} from './dissolve';
import menuHero from './assets/menu/hero.jpg';
import logoArt from './assets/menu/logo.webp';
import cardLocal from './assets/menu/card_local.jpg';
import cardAi from './assets/menu/card_ai.jpg';
import cardOnline from './assets/menu/card_online.jpg';
import {
  noInput,
  readPrimaryInput,
  serializeGameState,
  applyGameState,
} from './sync';
import {
  createGameState,
  stepGame,
  getMuzzle,
  seriesLive,
} from './simulation';

// --- Presentation constants ------------------------------------------------
// Module scope, not component scope: these never depend on props or state, and
// re-creating them on every render would also defeat the vignette cache below.

const GREEN = '#00ff41';
const RED = '#ff4757';

/** Team colour for a slot. */
const teamColor = (playerId) => (playerId === 'player1' ? GREEN : RED);

/**
 * Camera response per simulation event, keyed by the sound that announces it.
 *   shake — peak camera displacement in canvas pixels
 *   flash — peak opacity of a full-canvas colour wash
 * Events absent from this table are audio-only.
 */
const IMPACTS = {
  vaporize: { shake: 9, flash: 0.35, color: '180, 255, 255' },
  // Melee connects. Lighter than a kill, because it happens six times a fight
  // rather than once — at kill weight the screen never stops moving.
  hit: { shake: 4, flash: 0.1, color: '255, 255, 255' },
  // A block is a non-event for health, so it gets a nudge and no wash — but it
  // needs *something*, or a blocked hit is indistinguishable from a whiff.
  block: { shake: 2, flash: 0 },
  dismount: { shake: 4, flash: 0 },
  beam: { shake: 3, flash: 0.12, color: '0, 255, 255' },
  shock: { shake: 3, flash: 0.15, color: '255, 255, 0' },
  advance: { shake: 5, flash: 0 }, // the portal wipe supplies its own colour
  win: { shake: 10, flash: 0.3, color: '255, 255, 255' },
};

// Rim-light offsets, in canvas pixels. Four cardinal directions at a single
// pixel, kept faint on purpose.
//
// This used to be eight directions at half alpha, tuned for older art that was
// almost entirely dark navy and needed the outline to carry team identity. The
// current poses wear bright team-coloured neon down the suit and across the
// visor, so the art identifies itself — and a limb only two or three pixels
// wide, haloed from eight sides, stops being pixel art and becomes a glowing
// wireframe of itself.
const RIM_OFFSETS = [[-1, 0], [1, 0], [0, -1], [0, 1]];
const RIM_ALPHA = 0.3;

/**
 * Landing-screen briefing. Kept short and scannable on purpose: the old menu
 * spelled the rules out in full paragraphs, which ran past the bottom of the
 * viewport and hid the last two entirely.
 *
 * Only the essentials — the complete key list, the ordnance and the melee table
 * live one key away in ControlsScreen, which is the thing to keep exhaustive.
 * These eight fill the grid exactly twice over; a ninth leaves a ragged row.
 *
 * Each entry carries a line icon — SVG path data on a 24x24 grid, stroked in
 * the briefing's green — so a player scanning the strip finds a rule by its
 * shape before reading it.
 */
const BRIEFING = [
  { title: 'Feet Mode', body: 'Arrows to hover · Q kicks the board away',
    icon: 'M3 14h18l-2 3H5z M8 14v-5 M16 14v-5 M7 20l-1 2 M17 20l1 2' },
  { title: 'Hands Mode', body: 'Space fires the beam · Q throws the board',
    icon: 'M12 2v5 M12 17v5 M2 12h5 M17 12h5 M12 8a4 4 0 1 1 0 8a4 4 0 1 1 0-8z' },
  { title: 'Switch', body: 'W swaps feet ⇄ hands · Shift guards on foot',
    icon: 'M4 8h15 M15 4l4 4-4 4 M20 16H5 M9 12l-4 4 4 4' },
  { title: 'Beams', body: 'Instant kill, but the muzzle charges first',
    icon: 'M12 3v18 M3 12h18 M5.6 5.6l12.8 12.8 M18.4 5.6L5.6 18.4' },
  { title: 'Cover', body: 'Rooftops stop beams — hide, climb, stand on them',
    icon: 'M12 3l8 4.5v9L12 21l-8-4.5v-9z M4 7.5l8 4.5 8-4.5 M12 12v9' },
  { title: 'Boards', body: 'A kicked board unseats a rider, kills one on foot',
    icon: 'M7 17l13-7 1 2-13 7z M2 9h5 M3 13h4' },
  { title: 'On Foot', body: 'Charge a disarmed enemy — faster closer wins',
    icon: 'M5 6l6 6-6 6 M13 6l6 6-6 6' },
  { title: 'Goal', body: 'Push them back screen by screen to the portal',
    icon: 'M12 4a8 8 0 1 1 0 16a8 8 0 1 1 0-16z M12 8a4 4 0 1 1 0 8a4 4 0 1 1 0-8z M12 11.5v1' },
];

// Card art for each mode, keyed by mode id.
const MODE_ART = { local: cardLocal, ai: cardAi, online: cardOnline };

// How long the firing stance stays in its recoil frame after a shot, in ms.
// Long enough to register at a glance, short enough that the rider is back on
// aim well before the beam can be fired again.
const RECOIL_MS = 220;

// Local y the whole figure spins and squashes about — the body's middle,
// roughly hip height above the feet.
const FLIP_PIVOT = -25;

// Where the hips sit as a fraction of a pose's height, measured down from the
// top. Leaning hinges here so the body pivots off its legs rather than swinging
// around its own centre like a signpost.
const HIP_HEIGHT_FRAC = 0.55;

// Board-handling animation shape, in canvas pixels and radians. MOUNT_RISE is
// how far below the deck the board starts its swing up on a mount; SWING_ARC is
// how far the board rolls through the middle of a draw or stow, so it sweeps on
// an arc instead of sliding along a straight line.
//
// MOUNT_RISE is deliberately small. A rider mounts standing on the ground, so a
// board that starts far below the boots starts underneath the floor and swings
// up through it. Most of the step-on read comes from the rider rising onto the
// deck as it settles (see `settle` in drawPlayerSprite) rather than from the
// board travelling a long way.
const MOUNT_RISE = 10;
const MOUNT_TILT = 0.3;
const SWING_ARC = 0.55;

/**
 * Does this device have a real pointer?
 *
 * Pointer steering is a desktop affordance. On a touch screen every tap would
 * read as a click on the arena and silently seize movement control, so the
 * whole feature stays off unless a fine pointer is present.
 *
 * @returns {boolean}
 */
function hasFinePointer() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: fine)').matches;
}

// Primary-scheme movement keys. Pressing any of them hands movement back from
// the mouse to the keyboard.
/**
 * Keys the game owns during a match, so the browser's defaults can be
 * suppressed for exactly these and nothing else.
 */
const GAME_KEYS = new Set([
  'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' ', 'q', 'w', 'e', 'r',
]);

const STEER_RELEASE_KEYS = new Set(['arrowup', 'arrowleft', 'arrowdown', 'arrowright']);

// The in-match key strip: the whole keyboard scheme at a glance, in the same
// order as readPrimaryInput in sync.js, which is the only scheme there is.
// Keep the two in step — this is what players read to learn it.
const KEY_HINTS = [
  { keys: ['←', '→', '↑', '↓'], label: 'Move' },
  { keys: ['Space'], label: 'Attack' },
  { keys: ['W'], label: 'Switch' },
  { keys: ['Q'], label: 'Board' },
  { keys: ['Shift'], label: 'Guard' },
  { keys: ['E'], label: 'Cluster' },
  { keys: ['R'], label: 'EMP' },
];

// Speed caps the pointer-steering controller aims within. Read from the same
// constants the keyboard path accelerates toward, so mouse and keyboard drive
// the rider to identical top speeds.
const STEER_LIMITS = {
  boardSpeed: BOARD_SPEED, climbSpeed: BOARD_CLIMB_SPEED, playerSpeed: PLAYER_SPEED,
};

// On-foot state thresholds, in px/frame. Below MOVING_VX the rider is at rest;
// above AIRBORNE_VY they have left the ground.
const MOVING_VX = 0.4;
const AIRBORNE_VY = 1.2;

// Particle colour by type. Sizes are handled at the draw site.
const PARTICLE_COLORS = {
  hit: '255, 71, 87',
  electric: '255, 255, 0',
  beam: '0, 255, 255',
  recall: '0, 255, 65',
  advance: '255, 215, 0',
  victory: '255, 255, 255',
  respawn: '255, 255, 255',
  block: '190, 225, 255',
};

// Tug-of-war HUD geometry.
const TUG_BAR_WIDTH = 320;
const TUG_BAR_HEIGHT = 10;
const TUG_BAR_Y = 26;

// Cached because the canvas dimensions never change.
let vignetteGradient = null;
// Frame the cached gradient was built for, so a resize rebuilds it.
let vignetteKey = '';

/** The cell's top edge, in world y, before any perspective is applied. */
const CELL_TOP_Y = CEILING_Y + BODY_TOP - 22;

/** Depth of the cell's front face (see cellGeometry). */
const CELL_FRONT_DEPTH = 0.2;

/**
 * Where the cell's FRONT top girder projects to, for a viewer at `eye`. This
 * is the cell's nearest roof line and the champion's stage in the victory
 * scene, and it moves as the eye moves — a roof seen from above is not where
 * a roof seen from below is.
 *
 * @param {number} eye Eye level in world y.
 * @returns {number} World y of the front top girder.
 */
const cellRoofY = (eye) => CELL_TOP_Y + (eye - CELL_TOP_Y) * -CELL_FRONT_DEPTH;

/** How far down the frame the cell's roof line sits in the victory shot. */
const VICTORY_ROOF_FRACTION = 0.8;

/**
 * How much of the frame's height the champion fills in the victory shot. The
 * camera cranes in to this rather than keeping the fight's zoom: the fight is
 * framed for two fighters across a room, and at that distance the winner on
 * the roof was a speck under their own headline — the victory mock-up makes
 * them the centrepiece. Measured against the standing sprite's own height
 * (GEOMETRY), not the hitbox, which reaches well above the drawn head.
 */
const VICTORY_CHAMPION_SHARE = 0.2;

/** Ease for the dolly: gentle out of the old room, gentle into the new. */
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

// Read once per page load: whether the menu offers Local Multiplayer (see
// features.js). Off, vs AI is the first mode on the menu and the default.
const LOCAL_MULTIPLAYER = localMultiplayerEnabled();
const DEFAULT_MODE = LOCAL_MULTIPLAYER ? 'local' : 'ai';

const App = () => {
  const canvasRef = useRef(null);
  const gameStateRef = useRef(createGameState());

  const keysRef = useRef({});
  const keyPressedRef = useRef({}); // Track key press events (not held keys)
  // Camera shake + screen flash, driven by impact events. Purely presentational
  // and client-local, so it lives outside the synced game state — both the host
  // and the online guest raise it from the same sound-event names.
  const impactRef = useRef({ shake: 0, flash: 0, flashColor: '255, 255, 255' });
  // Dolly played when the fight breaks into a new screen: the camera travels
  // from the old room into the new one, through the shared doorway, with both
  // rooms drawn as one continuous corridor. Client-local like the impact
  // effects: the host raises it as it advances, and the guest raises its own
  // when a snapshot shows the screen changed, so it animates even across the
  // frames where the host has paused broadcasting.
  //   time       ms of transition left (0 = idle)
  //   fromScreen the screen being left, drawn as the trailing room
  //   direction  +1 pushing right, -1 pushing left
  //   startX/Y/Zoom  the camera at the moment of the cut (old-room framing),
  //                  the dolly's departure point
  const transitionRef = useRef({
    time: 0, fromScreen: START_SCREEN, direction: 1,
    startX: screenWidth(START_SCREEN) / 2, startY: 300, startZoom: 1,
  });
  // Victory-scene dressing: firework sparks in the sky over the cell. Purely
  // presentational and client-local — the online guest stages its own show
  // from the synced winner.
  const victoryRef = useRef({ sparks: [], nextBurst: 0 });
  // How far the scene has crossed from the fight's eye level to the victory
  // shot's. 0 = HORIZON_Y (a standing fighter's head, the approved gameplay
  // perspective); 1 = the camera's own height, which is what makes the
  // victory camera LEVEL with the champion on the roof rather than looking
  // up at him from the deck. Eased so the win does not pop.
  const victoryEyeRef = useRef(0);
  // The eye level the whole scene is currently drawn from — the cell's
  // vanishing point, the city's horizon glow and the deck plating all read
  // this one value. Resolved once per frame from victoryEyeRef.
  const sceneEyeRef = useRef(HORIZON_Y);
  // Free-running counter driving idle bobs and run cycles. Cosmetic only, so
  // it ticks on the render clock and never needs to be synced or simulated.
  const animPhaseRef = useRef(0);
  const [gameMode, setGameMode] = useState(DEFAULT_MODE);
  const [difficulty, setDifficulty] = useState('medium'); // vs-AI difficulty
  const [gameStarted, setGameStarted] = useState(false);
  const [inLobby, setInLobby] = useState(false); // showing the online lobby
  /**
   * Room code from a shared invite link (`?room=CODE`).
   *
   * Read once, during the first render, and stripped from the address bar the
   * moment it is read. It is a one-shot instruction rather than state: left in
   * the URL, reloading after the match — or restoring the tab days later —
   * would send the player back into a room that no longer exists.
   */
  const [invitedRoom] = useState(() => {
    const code = new URLSearchParams(window.location.search).get('room');
    if (!code) return '';
    window.history.replaceState({}, '', window.location.pathname);
    // Codes are lowercase hex; a link that has been through a system which
    // capitalises text still has to resolve to the same room.
    return code.trim().toLowerCase();
  });
  // Pre-fight controls overlay: state drives the overlay, the ref pauses the
  // simulation (the game loop closes over refs, not state).
  // Whatever panel is over the arena, if any:
  //   'intro'     — the pre-fight briefing, shown once per match
  //   'reference' — the controls, pulled up mid-match (HUD button or H)
  //   'pause'     — the in-match menu: resume, restart, quit
  // The ref mirrors the state because the global key handler is registered once
  // and would otherwise close over a stale value forever.
  const [overlay, setOverlay] = useState(null);
  const overlayRef = useRef(null);
  const controlsHoldRef = useRef(false);
  // Mirrors `gameStarted` for the same reason.
  const gameStartedRef = useRef(false);
  // Playtest bridge (playtest.js). Its first call takes the clock away from
  // real time: the game loop then only draws, and the match moves by step().
  const manualClockRef = useRef(false);
  // Game time handed to step() since the last reset, and the fixed steps run
  // for it. Counted whole so step(1000) is exactly 60 steps, never 59.
  const playtestClockRef = useRef({ ms: 0, steps: 0 });
  const playtestSeedRef = useRef(null);
  /**
   * The drawing frame: the 800x600 play area plus however much extra the
   * device's shape allows, filled with scenery. See viewport.js.
   *
   * Duplicated into a ref because the render loop runs from a closure that does
   * not re-subscribe on state changes, and it needs the current value every
   * frame rather than the one captured when the loop started.
   */
  const [view, setView] = useState(() => computeView(
    typeof window === 'undefined' ? 1280 : window.innerWidth,
    typeof window === 'undefined' ? 720 : window.innerHeight
  ));
  const viewRef = useRef(view);
  /**
   * The canvas's box on screen, in CSS px — the whole viewport, except on an
   * upright phone where the lower half is left to the thumbs (see stageBox).
   * The frame above is computed FROM this, so the two cannot disagree and
   * stretch the art between them.
   */
  const [stage, setStage] = useState(() => stageBox(
    typeof window === 'undefined' ? 1280 : window.innerWidth,
    typeof window === 'undefined' ? 720 : window.innerHeight
  ));
  const stageRef = useRef(stage);
  // The live camera. A ref, not state: it changes every frame and nothing in
  // the DOM depends on it.
  const cameraRef = useRef(createCamera());
  // Screen the camera was last composed for, so a screen change can cut rather
  // than pan.
  const cameraScreenRef = useRef(3);

  // Whether a user gesture has unlocked audio yet. Browsers refuse to play
  // anything before one, and the effect that follows the screen runs on mount
  // — long before the player has touched the page.
  const audioUnlockedRef = useRef(false);
  // Mirrors `endState`, so the per-frame comparison above costs no re-render.
  const endStateRef = useRef({
    winner: null, disconnected: false, midSeries: false, summary: null,
  });
  // How far down the page the end screen's console starts (CSS px), so the
  // victory camera can keep the champion above it. Infinity when it is not
  // over the arena.
  const endConsoleTopRef = useRef(Infinity);
  const reportEndConsoleTop = useCallback((px) => { endConsoleTopRef.current = px; }, []);
  // What the touch pads currently mean. Mirrored out of the game ref for the
  // same reason as endState: the pads are DOM, and a ref changing re-renders
  // nothing — without this the icons would keep showing the last state some
  // unrelated re-render happened to catch them in.
  /**
   * Everything the on-screen buttons draw themselves from.
   *
   * The readiness values are fractions of "fully available", which is what a
   * ring around a button can show without a number in it: a full ring is ready,
   * a partial one is filling, and how fast it moves says how long is left.
   *
   * Quantised to fortieths on the way out. That is not a rendering shortcut, it
   * is the throttle — the caller re-renders when this object changes, so
   * rounding the only continuously-moving fields is what stops a cooldown from
   * costing sixty React renders a second. A fortieth of the shortest clock here
   * is 150ms, which is finer than the eye tracks on a sweeping arc.
   */
  const step40 = (v) => Math.round(Math.max(0, Math.min(1, v)) * 40) / 40;

  /**
   * How ready each of a fighter's abilities is, as a fraction of "available".
   *
   * ONE definition, read by both readouts. The phone draws these as rings around
   * its buttons and the desktop draws them as bars beside the tug bar, and the
   * two must agree — a player who learns the game on one and plays it on the
   * other is entitled to the same numbers.
   *
   * `usable` is not simply `ready === 1`: a magazine with two shells left is
   * perfectly usable at two thirds of a ring.
   */
  const abilityStatus = (p) => ({
    // The board button itself can be on a clock — a pulse takes it away for 30s.
    board: {
      ready: step40(p.boardDown > 0 ? 1 - p.boardDown / EMP_BOARD_DOWN_TIME : 1),
      usable: p.boardDown <= 0,
    },
    beam: {
      ready: step40(p.beamRecharge > 0 ? 1 - p.beamRecharge / BEAM_RECHARGE_TIME : 1),
      usable: p.beamRecharge <= 0,
    },
    // Shells count as thirds, and the one being rebuilt as its own fraction of a
    // third, so the reading grows smoothly through a reload rather than jumping.
    cluster: {
      ready: step40((p.clusterAmmo
        + (p.clusterTimer > 0 ? 1 - p.clusterTimer / CLUSTER_RECHARGE : 0)) / CLUSTER_MAX),
      usable: p.clusterAmmo > 0,
      count: p.clusterAmmo,
    },
    emp: {
      ready: step40(p.empAmmo > 0
        ? 1 : (p.empTimer > 0 ? 1 - p.empTimer / EMP_RECHARGE : 0)),
      usable: p.empAmmo > 0,
    },
  });

  /**
   * Everything the on-screen buttons draw themselves from.
   *
   * Quantised to fortieths by abilityStatus, and that is the throttle rather
   * than a rendering shortcut: the caller re-renders when this object changes,
   * so rounding the only continuously-moving fields is what stops a cooldown
   * costing sixty React renders a second.
   */
  const padSnapshot = (me) => {
    const a = abilityStatus(me);
    return {
      onBoard: me.onBoard,
      boardMode: me.boardMode,
      // The console's ON FOOT / BOARD MODE switch does different things for a
      // board on your back and a board lying in the arena.
      holstered: !!me.holstered,
      clusterAmmo: me.clusterAmmo,
      empAmmo: me.empAmmo,
      boardReady: a.board.ready,
      beamReady: a.beam.ready,
      clusterReady: a.cluster.ready,
      empReady: a.emp.ready,
    };
  };

  const PAD_STATE_START = {
    onBoard: true,
    boardMode: 'feet',
    holstered: false,
    clusterAmmo: CLUSTER_MAX,
    empAmmo: EMP_MAX,
    boardReady: 1,
    beamReady: 1,
    clusterReady: 1,
    empReady: 1,
  };
  const [padState, setPadState] = useState(PAD_STATE_START);
  const padStateRef = useRef(PAD_STATE_START);

  /**
   * Which fighter this device drives: the online slot it was dealt, otherwise
   * green. The touch console and the phone header both read their fighter
   * through this — reading player 1 unconditionally, as the pads once did,
   * showed an online guest (who is red) the HOST's cooldowns.
   *
   * @param {object} game
   * @returns {'player1'|'player2'}
   */
  const localSlot = (game) => (
    game.gameMode === 'online' && onlineRef.current
      ? onlineRef.current.playerSlot
      : 'player1'
  );

  /**
   * Everything the phone's match header draws (see MobileHud), flattened so a
   * change check is a plain field comparison — the same throttle as
   * padSnapshot, and quantised by the same abilityStatus.
   *
   * @param {object} game
   * @returns {object}
   */
  const hudSnapshot = (game) => {
    const out = {
      screen: game.currentScreen,
      unlocked: !!game.advancementUnlocked,
      advancing: game.advancementUnlocked ? game.advancingPlayer : null,
      seriesGame: game.series ? game.series.game : 0,
      seriesP1: game.series ? game.series.wins.player1 : 0,
      seriesP2: game.series ? game.series.wins.player2 : 0,
    };
    [['p1', game.players.player1], ['p2', game.players.player2]].forEach(([k, p]) => {
      const a = abilityStatus(p);
      out[`${k}hp`] = p.hp;
      out[`${k}lethal`] = p.hp <= MOVES.punch.damage;
      out[`${k}board`] = a.board.ready;
      out[`${k}beam`] = a.beam.ready;
      out[`${k}cluster`] = a.cluster.ready;
      out[`${k}emp`] = a.emp.ready;
    });
    return out;
  };
  const [hud, setHud] = useState(null);
  const hudRef = useRef(null);
  // Set every render: whether the phone header is standing in for the canvas
  // HUD, which renderGame then leaves undrawn.
  const mobileHudRef = useRef(false);
  // How far down the page that header reaches, in CSS px (0 when it is not
  // up). The camera frames the fight below it.
  const hudInsetRef = useRef(0);
  // Stable, so the header's measuring effect does not re-run every render.
  const reportHudInset = useCallback((px) => { hudInsetRef.current = px; }, []);

  /**
   * The phone header's reach converted to FRAME px — the canvas is drawn at
   * the frame's resolution and scaled to its CSS box, so the two differ by
   * that scale — plus a little air, so a rider's head does not graze it.
   *
   * @returns {number}
   */
  const hudInsetFrame = () => {
    const canvas = canvasRef.current;
    if (!hudInsetRef.current || !canvas || !canvas.clientHeight) return 0;
    return (hudInsetRef.current + 6) * (viewRef.current.height / canvas.clientHeight);
  };
  // On-screen controls, for devices whose primary pointer is a thumb.
  const [touchPrimary] = useState(isTouchPrimary);
  // Landscape check, re-evaluated on rotate. The arena is 4:3 and the controls
  // need the bottom corners, so portrait is genuinely unplayable rather than
  // merely cramped — better to say so than to render something unusable.
  // Whether the match is over, mirrored out of the game ref into React so DOM
  // overlays can respond to it. The canvas end screen reads the ref directly;
  // a button cannot, because a ref changing re-renders nothing.
  const [endState, setEndState] = useState({
    winner: null, disconnected: false, midSeries: false, summary: null,
  });
  const [portrait, setPortrait] = useState(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth
  );
  /**
   * Desktop pointer steering. The cursor drives the local player's movement.
   *
   * Engaged by clicking the arena and dropped the moment a movement key is
   * pressed — deliberately explicit in both directions. Engaging on mere mouse
   * movement would wrench the rider away from a player who nudged their mouse
   * mid-fight, which is the worst possible moment to lose control.
   *
   * Position is kept in canvas pixels. The ref is what the game loop reads; the
   * state exists only to render the on-screen indicator.
   */
  const pointerRef = useRef({ x: 0, y: 0, active: false });
  const [pointerSteering, setPointerSteering] = useState(false);
  // Whether the landing screen's briefing starts unfolded — see Fold.js.
  const roomy = useRoomyScreen();
  // All-time win record for the currently selected matchup, shown on the menu.
  const [record, setRecord] = useState(() => getRecord(DEFAULT_MODE, 'medium'));
  // Play-money balance, mirrored into React state so the menu re-renders when a
  // series settles. wallet.js remains the source of truth — this is a view of
  // it, refreshed wherever the balance can have moved.
  const [balance, setBalance] = useState(getBalance);
  // What the player is putting up on the next ranked series, and whether ranked
  // is armed at all. Ranked is opt-in: an ordinary vs-AI match still costs and
  // pays nothing, so nobody loses money by pressing Start out of habit.
  const [ranked, setRanked] = useState(false);
  const [stake, setStake] = useState(50);
  // Audio preference, remembered across sessions. Someone who muted the game
  // once should not have to mute it again every time they open it.
  const [muted, setMutedState] = useState(() => {
    try {
      return localStorage.getItem('skydrop_muted') === '1';
    } catch (err) {
      return false; // private mode / storage disabled
    }
  });

  // Live online session, set when a match starts from the lobby. Held in refs
  // (not state) so the game loop can read them without re-subscribing.
  //   netRef:    the net.js connection handle ({ send, close, raw }).
  //   onlineRef: { roomId, playerId, playerSlot } for this client.
  const netRef = useRef(null);
  const onlineRef = useRef(null);
  // Latest input received from the guest (host reads this to drive red).
  const remoteInputRef = useRef(noInput());
  // Previous raw button states per controller, so a held button reads as one
  // press. A ref because it changes every frame and nothing renders from it.
  const padPrevRef = useRef({ player1: {}, player2: {} });

  // Refresh the displayed record whenever we land on the menu or switch what we
  // are about to play, so a match just played shows the up-to-date tally. The
  // AI difficulty is part of that: each tier keeps its own record, so picking a
  // different opponent has to swap the number shown beside it.
  useEffect(() => {
    if (!gameStarted) setRecord(getRecord(gameMode, difficulty));
  }, [gameStarted, gameMode, difficulty]);

  useEffect(() => { gameStartedRef.current = gameStarted; }, [gameStarted]);

  // Unlock audio on the player's first interaction of any kind, then start the
  // menu theme. Without this the landing screen would be permanently silent:
  // its only button is Start, and by the time that fires we are already in a
  // match. Registered once and removed the moment it fires.
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      initAudio();
      playMusic(gameStartedRef.current ? 'fight' : 'menu');
      preloadMusic(gameStartedRef.current ? 'menu' : 'fight');
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Re-fit the frame whenever the window changes shape.
  useEffect(() => {
    const refit = () => {
      // Orientation is read straight from the window rather than from the
      // `portrait` state, because a rotation changes both and there is no
      // ordering guarantee between the two listeners — reading the state here
      // would size the arena for the orientation it just left.
      const thumbRoom = touchPrimary
        && window.innerHeight > window.innerWidth;
      const box = stageBox(window.innerWidth, window.innerHeight, thumbRoom);
      if (stageChanged(stageRef.current, box)) {
        stageRef.current = box;
        setStage(box);
      }
      const next = computeView(box.width, box.height);
      if (!viewChanged(viewRef.current, next)) return;
      viewRef.current = next;
      setView(next);
    };
    refit();
    window.addEventListener('resize', refit);
    window.addEventListener('orientationchange', refit);
    return () => {
      window.removeEventListener('resize', refit);
      window.removeEventListener('orientationchange', refit);
    };
  }, [touchPrimary]);

  // Track orientation so the rotate prompt can appear and clear live.
  useEffect(() => {
    if (!touchPrimary) return undefined;
    const check = () => setPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, [touchPrimary]);

  // Keep the mute preference, the audio graph and storage in step.
  useEffect(() => {
    setMuted(muted);
    try {
      localStorage.setItem('skydrop_muted', muted ? '1' : '0');
    } catch (err) {
      // Storage unavailable — the setting simply won't survive a reload.
    }
  }, [muted]);

  // The score follows the screen. Declared as "what belongs here" rather than
  // as transitions, so nothing has to remember what was playing before.
  useEffect(() => {
    if (!audioUnlockedRef.current) return;
    playMusic(gameStarted ? 'fight' : 'menu');
    // Pull the other track down while this one plays, so the cue it is wanted
    // for is not preceded by a silent download.
    preloadMusic(gameStarted ? 'menu' : 'fight');
  }, [gameStarted]);

  // An invite link skips the menu entirely and opens the lobby on its join
  // step. The mode is set too, so backing out of the lobby lands on a menu that
  // still has Online selected rather than silently reverting to Local.
  useEffect(() => {
    if (!invitedRoom) return;
    setGameMode('online');
    setInLobby(true);
  }, [invitedRoom]);

  // Read-only hook for the end-to-end playtests. Everything the game draws
  // lives on a canvas, so there is no DOM for a test to assert against; without
  // this the suite has to infer where a rider is by hunting for team-coloured
  // pixels, which the neon cityscape backgrounds defeat.
  useEffect(() => {
    window.__skydrop = {
      getState: () => gameStateRef.current,
      // The touch controls work by synthesising key state, so the only way to
      // assert on them is to read the same maps they write into.
      keys: () => keysRef.current,
      // The canvas is no longer the play area — it is a frame the 800x600 play
      // area is mapped into by a moving, zooming camera. Anything asserting on
      // drawn pixels converts through these, which are the same functions the
      // renderer and the pointer path use, so they cannot disagree.
      view: () => viewRef.current,
      camera: () => cameraRef.current,
      worldToFrame: (x, y) => worldToFrame(
        cameraRef.current, viewRef.current.width, viewRef.current.height, x, y
      ),
      // The inverse, which the pointer path already steers by. Needed because
      // the camera zooms to the action: a test that wants to aim at somewhere
      // VISIBLE has to pick the point in frame space and ask what world
      // coordinate that is, since most of the arena is off-screen most of the
      // time and a cursor over open space steers nothing.
      frameToWorld: (x, y) => frameToWorld(
        cameraRef.current, viewRef.current.width, viewRef.current.height, x, y
      ),
      // The world point the rider is steering to. Converted from the cursor
      // ONCE, when the mouse moves, so it does NOT follow the cursor as the
      // camera pans afterwards — a test that recomputes the aim from the
      // cursor's screen position is measuring a point the game never aimed at,
      // and reads the resulting gap as a controller that stalls short.
      pointer: () => ({ ...pointerRef.current }),
      // The play-money balance, read through wallet.js rather than straight out
      // of localStorage, so a test cannot disagree with the game about what a
      // corrupt or absent value means.
      balance: () => getBalance(),
      // The dolly's state, for tests that need to know a transition is
      // mid-flight (its buffers are gone — the rooms are drawn live now).
      transition: () => ({ ...transitionRef.current }),
      // The arena's shape. Layouts are validated at module load and a piece
      // that breaks a rule is dropped rather than drawn wrong, so a level
      // edit can silently lose an obstacle; this is what lets a test notice.
      widths: () => [...SCREEN_WIDTHS],
      obstacles: (screen) => getObstacles(screen).map((o) => ({ ...o })),
    };
    return () => { delete window.__skydrop; };
  }, []);

  // Playtest bridge (playtest.js): start an exact match, advance exact game
  // time, read the state. Like __skydrop it closes over the first render, which
  // is safe because everything it calls reads refs or stable setters.
  useEffect(() => {
    if (!playtestRequested(window.location.search)) return undefined;
    const mode = playtestMode(window.location.search);

    /** Where the match stands, from the same flags the screens use. */
    const phaseOf = (game) => {
      if (!gameStartedRef.current) return 'menu';
      if (overlayRef.current === 'intro') return 'intro';
      if (overlayRef.current) return 'paused';
      if (game.winner || game.disconnected) return 'over';
      if (game.hitStop > 0) return 'frozen';
      return 'playing';
    };
    const fighter = (p) => ({
      x: p.x, y: p.y, vx: p.vx, vy: p.vy, hp: p.hp,
      onBoard: p.onBoard, boardMode: p.boardMode, grounded: p.grounded,
      stunned: p.stunned, vaporizing: p.vaporizing,
      clusterAmmo: p.clusterAmmo, empAmmo: p.empAmmo,
    });

    return installPlaytestBridge({
      snapshot: () => {
        const game = gameStateRef.current;
        return {
          phase: phaseOf(game), mode: game.gameMode, difficulty: game.difficulty,
          screen: game.currentScreen, winner: game.winner,
          advancingPlayer: game.advancingPlayer, hitStop: game.hitStop,
          steps: playtestClockRef.current.steps, manualClock: manualClockRef.current,
          player1: fighter(game.players.player1), player2: fighter(game.players.player2),
          ordnance: game.ordnance.length, beams: game.beams.length,
        };
      },
      // Applies from the next reset(): the seed fixes the scatter and the AI.
      setSeed: (seed) => { playtestSeedRef.current = Number(seed) >>> 0; },
      // A fresh match in the URL's mode, past the briefing, on the manual
      // clock. Resolves once React has mounted the arena and drawn a frame.
      reset: () => {
        manualClockRef.current = true;
        playtestClockRef.current = { ms: 0, steps: 0 };
        gameStateRef.current = createGameState({
          gameMode: mode,
          difficulty: gameStateRef.current.difficulty,
          ...(playtestSeedRef.current !== null && { seed: playtestSeedRef.current }),
        });
        keysRef.current = {};
        impactRef.current.shake = 0;
        impactRef.current.flash = 0;
        transitionRef.current.time = 0;
        dismissControls();
        gameStartedRef.current = true;
        setGameMode(mode);
        setGameStarted(true);
        return new Promise((resolve) => {
          const ready = () => requestAnimationFrame(canvasRef.current ? () => resolve() : ready);
          ready();
        });
      },
      // Whole fixed steps through the same updateGame the loop runs, so held
      // keys apply and a paused or briefing-held match does not move.
      step: (ms) => {
        manualClockRef.current = true;
        const clock = playtestClockRef.current;
        clock.ms += Math.max(0, Number(ms) || 0);
        const target = Math.floor(clock.ms / STEP_MS + 1e-9);
        for (; clock.steps < target; clock.steps += 1) updateGame();
      },
    });
  }, []);

  // Game constants live in ./constants.js (imported above) so gameplay modules
  // share a single source of truth.

  // Initialize game
  useEffect(() => {
    // Start fetching background + sprite art immediately so it's ready by
    // first match.
    preloadParallax();
    preloadSprites();
    preloadControlsScreen();

    // Keyboard event listeners
    const handleKeyDown = (e) => {
      const keyLower = e.key.toLowerCase();
      
      // Only register as a "press" if key wasn't already held down
      if (!keysRef.current[keyLower]) {
        keyPressedRef.current[keyLower] = true;
      }
      
      keysRef.current[keyLower] = true;

      // The game's own keys must not also do what the browser does with them.
      // Space and the arrows scroll a page, and the arrows move focus between
      // controls — none of which anybody wants mid-match. Scoped to a live
      // match so the menus keep their ordinary keyboard behaviour.
      if (gameStartedRef.current && GAME_KEYS.has(keyLower)) {
        e.preventDefault();
      }

      // A movement key takes the wheel back from the mouse. Only the primary
      // scheme's keys: player 2's arrows must not disengage player 1's mouse.
      if (STEER_RELEASE_KEYS.has(keyLower)) setSteering(false);

      // Controls reference: H toggles it, Escape closes it. Only during a live
      // match — on the menu these keys mean nothing, and while the pre-fight
      // briefing is up any key already dismisses it.
      if (gameStartedRef.current) {
        if (keyLower === 'h') {
          toggleControlsReference();
        } else if (keyLower === 'escape') {
          // Escape backs out of whatever is open, and opens the menu when
          // nothing is: one key that always means "get me out of this".
          if (overlayRef.current === 'reference') toggleControlsReference();
          else togglePauseMenu();
        }
      }

      // Handle restart and menu keys when the game is over or the opponent left.
      const game = gameStateRef.current;
      if (game.winner || game.disconnected) {
        const session = onlineRef.current;
        const isOnline = game.gameMode === 'online' && !!session;
        if (keyLower === 'm') {
          // Return to menu (also tears down any online connection).
          returnToMenu();
        } else if (keyLower === 'r' && !game.disconnected) {
          // Online: only the host may restart — the guest re-syncs to host state,
          // so a guest-side restart would be immediately overwritten.
          if (!isOnline || session.playerSlot === 'player1') {
            // Mid-series, the same key carries on to the next game instead of
            // throwing the tally away — the stake is riding on all three. Once
            // it is decided, it offers the same stake again.
            if (seriesLive(game)) nextGameInSeries();
            else rematchSeries();
          }
        }
      }
    };
    
    const handleKeyUp = (e) => {
      keysRef.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  /**
   * Everything the end screen says, worked out once as the match ends.
   *
   * The rematch wording is decided here with the same rules the R key and the
   * button follow (rematchSeries / nextGameInSeries), so the screen can never
   * offer one thing and the key do another.
   *
   * @param {object} game
   * @returns {object|null} null while the match is still on.
   */
  const endSummary = (game) => {
    if (game.disconnected) {
      return {
        disconnected: true,
        headline: 'Opponent disconnected',
        team: '255, 71, 87',
      };
    }
    if (!game.winner) return null;

    const winnerName = game.winner === 'player1' ? 'Green' : 'Red';
    // Mid-series, this fighter has won a GAME, not the match — saying "wins"
    // over a series they may still lose is the wrong headline entirely.
    const midSeries = seriesLive(game);
    const rightName = game.gameMode === 'ai' ? 'AI' : 'Red';
    let score = null;
    let detail = null;
    let payout = null;
    if (game.series) {
      const { wins, target, stake } = game.series;
      score = { leftName: 'Green', left: wins.player1, right: wins.player2, rightName };
      detail = midSeries && stake > 0
        ? `First to ${target} · ${stake} at stake`
        : `Series · first to ${target}`;
      // A ranked run for nothing is a legitimate choice, and "+0 won" over it
      // is noise — the scoreline already says what happened.
      if (!midSeries && stake > 0 && game.payout) {
        const { change } = game.payout;
        payout = { won: change >= 0, text: change >= 0 ? `+${change} won` : `${change} lost` };
      }
    } else if (game.matchRecord) {
      score = {
        leftName: 'Green', left: game.matchRecord.player1,
        right: game.matchRecord.player2, rightName,
      };
    }

    const staked = !!game.series && game.series.stake > 0;
    return {
      disconnected: false,
      headline: midSeries ? `${winnerName} takes game ${game.series.game}` : `${winnerName} wins`,
      team: game.winner === 'player1' ? '0, 255, 90' : '255, 60, 80',
      score,
      detail,
      payout,
      rematchLabel: midSeries ? 'Next game' : 'Rematch',
      // What the rematch will cost: the same stake again, or — if the wallet
      // no longer covers it — nothing, and it says so.
      rematchNote: midSeries || !staked ? null
        : canCover(game.series.stake)
          ? `Stakes ${game.series.stake} again`
          : 'No stake — balance too low',
      // The online guest cannot restart: the host owns the rematch, and a
      // guest-side reset would be overwritten by the next snapshot.
      waitingForHost: game.gameMode === 'online'
        && onlineRef.current?.playerSlot === 'player2',
    };
  };

  /**
   * Where the roof line should land in the victory shot, as a fraction of the
   * frame's height: low in the frame, as VICTORY_ROOF_FRACTION has it, but
   * never behind the end screen's console when that sits over the arena — the
   * champion stands just above it instead.
   *
   * @returns {number}
   */
  const victoryRoofFraction = () => {
    const canvas = canvasRef.current;
    const top = endConsoleTopRef.current;
    if (!canvas || !Number.isFinite(top)) return VICTORY_ROOF_FRACTION;
    const box = canvas.getBoundingClientRect();
    const fraction = (top - box.top) / box.height - 0.04;
    return Math.max(0.5, Math.min(VICTORY_ROOF_FRACTION, fraction));
  };

  /**
   * Reset to a fresh match, preserving the selected mode and AI difficulty.
   *
   * Never re-stakes. Anything that abandons a series — quitting to the menu,
   * restarting from the pause panel — comes through here, and a stake already
   * placed is GONE: walking out of a series you are losing must not be a way to
   * get the money back, or every series becomes a free roll.
   *
   * @param {object|null} [series] Series to carry in, for a deliberate rematch.
   */
  const restartGame = (series = null) => {
    const { gameMode: mode, difficulty: level } = gameStateRef.current;
    gameStateRef.current = createGameState({
      gameMode: mode, difficulty: level, series,
    });
    setBalance(getBalance());
    impactRef.current.shake = 0;
    impactRef.current.flash = 0;
    transitionRef.current.time = 0;
  };

  /**
   * Play the same stake again after a series has been decided.
   *
   * Falls back to an unstaked rematch when the wallet no longer covers it —
   * quietly re-staking a balance that cannot pay is the one move a play-money
   * economy must never make. The end overlay says which of the two is on offer,
   * so the key never does something the screen did not advertise.
   */
  const rematchSeries = () => {
    const { series: last, difficulty: level } = gameStateRef.current;
    restartGame(last && canCover(last.stake) ? beginSeries(last.stake, level) : null);
  };

  /**
   * Take the stake and build the series record for a best-of-three.
   *
   * The money leaves the wallet here, at the start, rather than being settled
   * at the end: a stake that is only notionally committed can be spent from
   * another screen while the series it is riding on is still being played.
   *
   * @param {number} stake      Amount to put up; 0 for an unstaked ranked run.
   * @param {string} difficulty AI tier, which fixes the odds for the whole run.
   * @returns {object|null} the series record, or null if it could not be paid.
   */
  const beginSeries = (stake, difficulty) => {
    if (placeStake(stake) === null) return null;
    return {
      wins: { player1: 0, player2: 0 },
      target: 2, // best of three
      stake: Math.max(0, Math.floor(stake)),
      difficulty,
      game: 1,
      settled: false,
    };
  };

  /** Turn pointer steering on or off, keeping ref and indicator in step. */
  const setSteering = (on) => {
    if (pointerRef.current.active === on) return;
    pointerRef.current.active = on;
    setPointerSteering(on);
  };

  /**
   * Track the cursor in canvas pixels.
   *
   * The canvas is letterboxed by CSS (full height, automatic width), so client
   * coordinates have to be mapped through its rendered box — using the raw
   * event position would put the target further right the wider the window got.
   */
  const trackPointer = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // Client px -> frame px -> world px. The last step inverts the camera: the
    // play area is neither at the canvas origin nor drawn at 1:1, so without it
    // the rider flies to a point displaced by the scenery to its left and
    // scaled by however far the camera has pushed in.
    const view = viewRef.current;
    const frameX = (e.clientX - rect.left) * (view.width / rect.width);
    const frameY = (e.clientY - rect.top) * (view.height / rect.height);
    const world = frameToWorld(
      cameraRef.current, view.width, view.height, frameX, frameY
    );
    pointerRef.current.x = world.x;
    pointerRef.current.y = world.y;
  };

  /**
   * Build the local human's input frame.
   *
   * Movement comes from the pointer when it has the wheel; the action keys are
   * always read from the keyboard, so a player steering with the mouse still
   * kicks, fires and switches mode with their other hand. The keyboard scheme
   * itself is sync.js's readPrimaryInput — there is only one.
   */
  const readLocalInput = (player) => {
    const frame = readPrimaryInput(keysRef.current, keyPressedRef.current);
    if (!pointerRef.current.active || !player) return frame;
    // The pointer's ref doubles as steering's memory (steerToPointer).
    return {
      ...frame,
      ...steerToPointer(player, pointerRef.current, STEER_LIMITS, pointerRef.current),
    };
  };

  /** Set the controls overlay, keeping the ref the key handler reads in step. */
  const showOverlay = (view) => {
    overlayRef.current = view;
    setOverlay(view);
  };

  /** Show the pre-fight controls overlay and pause the simulation behind it. */
  const beginMatchIntro = () => {
    controlsHoldRef.current = true;
    showOverlay('intro');
  };

  /** Dismiss the controls overlay and let the fight begin. */
  const dismissControls = () => {
    controlsHoldRef.current = false;
    showOverlay(null);
    // Swallow the dismissing keypress so it doesn't fire a kick/mode-switch on
    // the first simulated frame. Held movement keys still apply naturally.
    keyPressedRef.current = {};
  };

  /**
   * Open or close the mid-match controls reference.
   *
   * Local and vs-AI matches pause behind it, the same as the pre-fight
   * briefing. Online matches deliberately do NOT: the hold gates the host's
   * simulation and the guest's input sending, so a player who left this open
   * would freeze their opponent's game. Online players read it while the fight
   * carries on, which the panel says plainly.
   */
  const toggleControlsReference = () => {
    // The pre-fight briefing owns the screen while it is up.
    if (overlayRef.current === 'intro') return;

    if (overlayRef.current === 'reference') {
      closeOverlay();
      return;
    }
    if (gameStateRef.current.gameMode !== 'online') controlsHoldRef.current = true;
    showOverlay('reference');
  };

  /**
   * Open or close the in-match menu (resume / restart / quit).
   *
   * Pauses on the same terms as the controls reference: offline freezes behind
   * it, online cannot without stalling the opponent.
   */
  const togglePauseMenu = () => {
    if (overlayRef.current === 'intro') return;
    if (overlayRef.current === 'pause') {
      closeOverlay();
      return;
    }
    if (gameStateRef.current.gameMode !== 'online') controlsHoldRef.current = true;
    showOverlay('pause');
  };

  /** Close whatever mid-match panel is open and let the fight resume. */
  const closeOverlay = () => {
    controlsHoldRef.current = false;
    showOverlay(null);
    // Swallow the closing keypress so it cannot also fire a kick or a switch.
    keyPressedRef.current = {};
  };

  /** Restart from the pause menu, then drop back into the fight. */
  const restartFromMenu = () => {
    restartGame();
    closeOverlay();
  };

  const returnToMenu = () => {
    // Tear down any live online session before leaving the match.
    if (netRef.current) {
      netRef.current.close();
      netRef.current = null;
    }
    onlineRef.current = null;
    controlsHoldRef.current = false;
    showOverlay(null);
    setInLobby(false);
    setGameStarted(false);
    // Reset game state
    restartGame();
  };

  /**
   * Raise the camera shake / screen flash for an event, taking the strongest
   * value if several land in the same frame (impacts overlap rather than
   * queue). Kept separate from playback so the online guest — which hears the
   * host's relayed events but runs no simulation — feels the same hits.
   * @param {string} name sound.js effect name.
   */
  const applyImpact = (name) => {
    const impact = IMPACTS[name];
    if (!impact) return;
    const state = impactRef.current;
    state.shake = Math.max(state.shake, impact.shake);
    if (impact.flash) {
      state.flash = Math.max(state.flash, impact.flash);
      state.flashColor = impact.color;
    }
  };

  /** Play a sound and fire its camera response together. */
  const playFeedback = (name) => {
    playSound(name);
    applyImpact(name);
  };

  /**
   * Begin the dolly for a screen change.
   *
   * Nothing is snapshotted: during the transition the renderer draws BOTH
   * rooms live, side by side in one continuous world, and glides an effective
   * camera from the old room's framing (captured here, before the camera
   * snaps to the new fight) into the new one. The cell's perspective stays
   * accurate the whole way because both rooms share one vanishing point that
   * travels with the dolly — see renderGame.
   *
   * @param {number} fromScreen Screen being left.
   * @param {number} direction +1 if the push was rightward, -1 if leftward.
   */
  const startScreenTransition = (fromScreen, direction) => {
    const cam = cameraRef.current;
    transitionRef.current = {
      time: SCREEN_TRANSITION_TIME,
      fromScreen,
      direction,
      // Old-room coordinates; the renderer re-bases them into the new room.
      startX: cam.x,
      startY: cam.y,
      startZoom: cam.zoom,
    };
  };

  /**
   * Play a simulation sound locally and, when hosting an online match, queue it
   * so the next snapshot relays it to the guest (whose own simulation never
   * runs). This is the simulation's fx.sound — see simFx.
   * @param {string} name sound.js effect name.
   */
  const gameSound = (name) => {
    playFeedback(name);
    const game = gameStateRef.current;
    if (game.gameMode === 'online' && onlineRef.current?.playerSlot === 'player1') {
      game.soundEvents.push(name);
    }
  };

  /**
   * Handle in-match network messages (installed after the lobby handshake).
   *
   * Host (player1): consumes the guest's input to drive red.
   * Guest (player2): applies the host's authoritative snapshot to local state.
   * The backend echoes every message to the whole room including the sender, so
   * the slot checks below also discard this client's own broadcasts.
   */
  const handleNetMessage = (msg) => {
    const session = onlineRef.current;
    if (!session) return;

    if (msg.type === 'game_action' && msg.data) {
      const { action } = msg.data;
      if (action === 'input' && session.playerSlot === 'player1') {
        // Latch edge-triggered flags (kick / mode switch): they're true for a
        // single guest frame, and two guest inputs can arrive between host
        // frames — a plain overwrite would drop the press. OR them in here and
        // clear them after the host consumes them in updateGame.
        const prev = remoteInputRef.current || noInput();
        const next = msg.data.input || noInput();
        remoteInputRef.current = {
          ...next,
          action: prev.action || next.action,
          switchMode: prev.switchMode || next.switchMode,
        };
      } else if (action === 'state' && session.playerSlot === 'player2') {
        const game = gameStateRef.current;
        const hadWinner = !!game.winner;
        const previousScreen = game.currentScreen;
        const sounds = applyGameState(game, msg.data.state);
        // Replay the host's simulation sounds locally, with their camera shake
        // and flash, since the guest never simulates the events itself.
        sounds.forEach(playFeedback);
        // A changed screen index means the host broke through: run the portal
        // wipe locally. The push direction is implied by which way the index
        // moved, so this needs nothing extra on the wire.
        if (game.currentScreen !== previousScreen) {
          startScreenTransition(
            previousScreen,
            game.currentScreen > previousScreen ? 1 : -1
          );
        }
        // Persist the win into this client's own tally exactly once, on the
        // snapshot where the winner first appears (the host records its own).
        if (!hadWinner && game.winner) {
          recordWin('online', game.winner);
        }
      }
    } else if (msg.type === 'player_disconnected' && msg.playerId !== session.playerId) {
      // Opponent left mid-match: freeze and surface a message (M returns to menu).
      gameStateRef.current.disconnected = true;
    }
  };

  /**
   * Begin an online match once the lobby has connected both players. Stashes the
   * live socket + this client's slot, swaps the socket's message handler over to
   * in-match sync, and starts the game loop. The connection stays open across
   * the match (torn down in returnToMenu).
   */
  const startOnlineMatch = ({ connection, roomId, playerId, playerSlot }) => {
    // The lobby button click already satisfies the audio-unlock gesture, but
    // calling initAudio() again is safe and keeps parity with local start.
    initAudio();
    audioUnlockedRef.current = true;
    netRef.current = connection;
    onlineRef.current = { roomId, playerId, playerSlot };
    remoteInputRef.current = noInput();
    gameStateRef.current.gameMode = 'online';
    // Hand the message stream from the lobby handshake to the sync handler.
    connection.setMessageHandler(handleNetMessage);
    setInLobby(false);
    beginMatchIntro();
    setGameStarted(true);
  };

  /**
   * Game loop — fixed simulation timestep, variable render rate.
   *
   * updateGame() advances exactly STEP_MS of simulated time per call, and an
   * accumulator runs however many steps the elapsed wall-clock time earned.
   * Previously the loop stepped once per animation frame while every timer
   * decremented by a hardcoded 16ms, which made the whole game run ~2.4x fast
   * on a 144Hz display. Rendering still happens once per frame.
   *
   * The returned cleanup cancels the frame request. Without it each match left
   * its loop running forever: returning to the menu and starting again stacked
   * a second loop on top of the first (measured 60 -> 120 -> 180 steps/sec over
   * three matches), so the simulation sped up with every match played.
   */
  useEffect(() => {
    if (!gameStarted) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let frameId = null;
    let lastTime = performance.now();
    let accumulator = 0;

    const gameLoop = (now) => {
      frameId = requestAnimationFrame(gameLoop);

      // Clamp the delta: a backgrounded tab hands back seconds of elapsed time,
      // which would otherwise fast-forward the match in a single frame.
      const elapsed = Math.min(now - lastTime, STEP_MS * MAX_CATCHUP_STEPS);
      lastTime = now;
      // Once the playtest bridge holds the clock, only its step() advances the
      // match; the loop keeps drawing.
      if (manualClockRef.current) {
        accumulator = 0;
      } else {
        accumulator += elapsed;
        while (accumulator >= STEP_MS) {
          updateGame();
          accumulator -= STEP_MS;
        }
      }

      // Advance the camera before drawing, so the frame is composed for the
      // positions about to be rendered rather than the previous ones.
      {
        const g = gameStateRef.current;
        const v = viewRef.current;
        // A win reframes the shot for the victory scene: the whole cell with
        // the winner on its roof and sky above for the fireworks. Eased, not
        // snapped — the pull-back from the final exchange is part of the
        // moment.
        const celebrating = g.winner && !g.disconnected;
        // The scene's eye crosses to the camera's own height for the victory
        // shot, and back for a rematch. Eased at the camera's pan rate so
        // the two stay in step and the shot is never drawn from an eye the
        // camera is not at — that disagreement is what made an earlier
        // version read as a perspective that did not match.
        victoryEyeRef.current +=
          ((celebrating ? 1 : 0) - victoryEyeRef.current) * 0.06;

        let target;
        if (celebrating) {
          // The victory shot: a LEVEL camera risen to roof height, at the
          // fight's own distance (zoom frozen where the match left it, so
          // nothing shrinks). Level matters — with the eye left down at the
          // fighters' height everything above it is by definition seen from
          // underneath, so panning up could only ever show the box's belly.
          // With the eye at the camera, the roof is seen from slightly above
          // and the champion is met face on.
          //
          // Solving the projection for "the roof line lands at
          // VICTORY_ROOF_FRACTION down the frame" gives the height to fly
          // to. cellRoofY moves with the eye, and here the eye IS this
          // camera, so both sides of that have to be resolved together.
          // roof(eye) = TOP*(1+D) - D*eye, and with eye = camY the roof's
          // screen height is (TOP - camY)*(1+D)*zoom + h/2, so:
          // Pushed in until the champion fills their share of the frame —
          // never pulled out past where the fight already was.
          const zoom = Math.max(
            cameraRef.current.zoom,
            (VICTORY_CHAMPION_SHARE * v.height) / GEOMETRY.idle.h
          );
          const spread = 1 + CELL_FRONT_DEPTH;
          target = {
            x: screenWidth(g.currentScreen) / 2,
            y: CELL_TOP_Y
              - (v.height * (victoryRoofFraction() - 0.5)) / (zoom * spread),
            zoom,
          };
        } else {
          target = cameraTarget(
            g.players.player1, g.players.player2, v.width, v.height,
            screenWidth(g.currentScreen), hudInsetFrame()
          );
        }
        // A screen change teleports both fighters, and easing across that reads
        // as the camera flying over the city rather than cutting to the next
        // cell.
        const snap = g.currentScreen !== cameraScreenRef.current;
        cameraScreenRef.current = g.currentScreen;
        advanceCamera(cameraRef.current, target, v.width, v.height, snap, hudInsetFrame());
      }

      renderGame(ctx);

      // Publish match-over state to React. Guarded on an actual change: this
      // runs every frame, and an unguarded setState here would re-render the
      // whole component sixty times a second.
      const over = gameStateRef.current;
      // `midSeries` rides along so the touch rematch button can say "Next Game"
      // rather than "Rematch" when there are still games to play.
      const midSeries = seriesLive(over);
      if (over.winner !== endStateRef.current.winner
          || !!over.disconnected !== endStateRef.current.disconnected
          || midSeries !== endStateRef.current.midSeries) {
        endStateRef.current = {
          winner: over.winner,
          disconnected: !!over.disconnected,
          midSeries,
          summary: endSummary(over),
        };
        setEndState(endStateRef.current);
      }

      // Same trick for the touch pads. Only on touch devices — on a desktop
      // this would be a re-render every time someone switched mode, for markup
      // that is not on screen.
      if (touchPrimary) {
        const next = padSnapshot(over.players[localSlot(over)]);
        // Re-render only when something the buttons draw actually moved. The
        // readiness fractions are quantised inside padSnapshot precisely so this
        // test can be a plain comparison and still throttle itself: a six-second
        // beam ring ticks a few times a second, a three-minute EMP ring every
        // few seconds, and neither costs a frame in between.
        const prev = padStateRef.current;
        if (Object.keys(next).some((k) => next[k] !== prev[k])) {
          padStateRef.current = next;
          setPadState(next);
        }
        // And the phone header, on the same terms.
        const nextHud = hudSnapshot(over);
        const prevHud = hudRef.current;
        if (!prevHud || Object.keys(nextHud).some((k) => nextHud[k] !== prevHud[k])) {
          hudRef.current = nextHud;
          setHud(nextHud);
        }
      }
    };

    frameId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(frameId);
  }, [gameStarted]);

  /**
   * One fixed step of the loop: the presentation's clocks, the network, and —
   * unless this client is an online guest — the simulation (see
   * simulation.js, which owns every rule of the fight).
   */
  const updateGame = () => {
    const game = gameStateRef.current;

    // Decay camera shake / flash here rather than in the renderer so impacts
    // fade over a fixed amount of simulated time instead of a fixed number of
    // display frames. Runs for the online guest too, which returns early below.
    const impact = impactRef.current;
    impact.shake *= 0.86;
    impact.flash *= 0.88;

    // The portal wipe advances on simulated time too, so it keeps sweeping
    // through the brief hold that follows a breakthrough (and, for the guest,
    // through the frames where the frozen host sends no snapshots).
    const transition = transitionRef.current;
    if (transition.time > 0) transition.time -= STEP_MS;

    // Pre-fight controls overlay is up: hold the whole simulation (and, for the
    // online guest, input sending) until it's dismissed.
    if (controlsHoldRef.current) return;

    const session = onlineRef.current;
    const isOnline = game.gameMode === 'online' && !!session;
    const isOnlineHost = isOnline && session.playerSlot === 'player1';
    const isOnlineGuest = isOnline && session.playerSlot === 'player2';

    // Online guest is a thin client: it never simulates. It forwards its local
    // input to the host and renders whatever authoritative snapshot arrives
    // (applied in handleNetMessage). Winner/disconnect come in via that snapshot.
    if (isOnlineGuest) {
      if (!game.winner && !game.disconnected && netRef.current) {
        // Steering resolves to key states here, on the guest, so the host stays
        // authoritative and the wire format is unchanged.
        const input = readLocalInput(game.players[session.playerSlot]);
        netRef.current.send({ type: 'game_action', data: { action: 'input', input } });
      }
      keyPressedRef.current = {};
      return;
    }

    // The fight itself. A frozen step (a decided game, a departed opponent, a
    // hit stop) asked nobody for input, so presses queued during it are kept
    // for the first step that does.
    if (!stepGame(game, readInput, simFx)) return;

    // Clear key pressed flags after processing
    keyPressedRef.current = {};

    // Update particles
    updateParticles();

    // Online host: broadcast the authoritative snapshot for the guest to
    // render, then drain the per-frame sound queue (each event must reach the
    // guest exactly once).
    if (isOnlineHost && netRef.current) {
      netRef.current.send({
        type: 'game_action',
        data: { action: 'state', state: serializeGameState(game) },
      });
      game.soundEvents.length = 0;
    }
  };

  /**
   * Where a fighter's input comes from this step. stepGame asks once per
   * fighter, at the moment that fighter gets to act.
   *
   * Player 2's source depends on the mode; read it from the game state rather
   * than React state so this never depends on which render the loop's closure
   * captured.
   *
   * @param {string} playerId 'player1' | 'player2'.
   * @param {object} player That fighter's state.
   * @param {object} game The whole game state.
   * @returns {?object} input frame, or null for no input this step.
   */
  const readInput = (playerId, player, game) => {
    const session = onlineRef.current;
    const isOnlineHost = game.gameMode === 'online' && !!session
      && session.playerSlot === 'player1';

    // A controller takes the slot it is assigned to, whatever that slot would
    // otherwise have read. Checked first so plugging one in mid-match just
    // works, and so the keyboard remains live for anyone without one.
    const pads = padCount();
    const padIndex = pads > 0 && game.gameMode !== 'online'
      ? padForSlot(playerId, pads, game.gameMode === 'local')
      : -1;
    const padInput = padIndex >= 0
      ? readGamepad(padIndex, padPrevRef.current[playerId])
      : null;

    if (padInput && (playerId === 'player1' || game.gameMode === 'local')) {
      return padInput;
    }
    if (playerId === 'player1') return readLocalInput(player);
    if (playerId === 'player2' && isOnlineHost) {
      // Host drives red from the guest's latest networked input, and clears
      // the latched one-shot flags as it hands them over — this step consumes
      // them, so the next one must not see them again.
      const remote = remoteInputRef.current || noInput();
      const input = { ...remote };
      remote.action = false;
      remote.switchMode = false;
      remote.punch = false;
      remote.kick = false;
      return input;
    }
    // Local player 2 has NO keyboard branch, deliberately. There is one
    // scheme in this game and both players use it, which two people at one
    // machine cannot do: a browser cannot tell two keyboards apart, so
    // identical keys would drive both fighters at once. Locally, player two
    // brings a controller (handled above); on two machines it is Online, where
    // each player already drives their own slot with the same scheme.
    if (playerId === 'player2' && game.gameMode === 'ai') {
      // AI controls for player 2 with error handling
      try {
        return getAIInput(player, game.players.player1, game);
      } catch (error) {
        console.warn('AI input error:', error);
        return null; // Fallback to no input if AI fails
      }
    }
    return null;
  };

  /**
   * A burst of sparks: the simulation's fx.particle. Cosmetic, so it may roll
   * Math.random freely — nothing the fight decides ever reads a particle.
   *
   * @param {number} x
   * @param {number} y
   * @param {string} type Key into PARTICLE_COLORS.
   */
  const createParticle = (x, y, type) => {
    const game = gameStateRef.current;

    for (let i = 0; i < 10; i++) {
      game.particles.push({
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8,
        life: 1000,
        type: type
      });
    }
  };

  /** Drift and fade the sparks, one step's worth. */
  const updateParticles = () => {
    const game = gameStateRef.current;

    game.particles = game.particles.filter(particle => {
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.life -= STEP_MS;
      particle.vx *= 0.98;
      particle.vy *= 0.98;

      return particle.life > 0;
    });
  };

  /**
   * The presentation's half of a breakthrough: the simulation's
   * fx.screenChange, called once the fighters are already in the new room.
   *
   * @param {number} fromScreen Screen being left.
   * @param {number} direction +1 if the push was rightward, -1 if leftward.
   * @param {string} advancerId Slot of the fighter who broke through.
   */
  const playScreenChange = (fromScreen, direction, advancerId) => {
    const game = gameStateRef.current;

    // Dolly into the new room; the old room's sparks do not come with it.
    startScreenTransition(fromScreen, direction);
    game.particles = [];

    // Sparks spilling off the gate the advancer just came through, in their
    // colour, thrown along the direction of travel.
    const gateX = direction > 0 ? 0 : screenWidth(game.currentScreen);
    const rgb = advancerId === 'player1' ? '0, 255, 65' : '255, 71, 87';
    for (let i = 0; i < 22; i++) {
      game.particles.push({
        x: gateX + direction * Math.random() * 40,
        y: CEILING_Y + Math.random() * (GROUND_Y - CEILING_Y),
        vx: direction * (2 + Math.random() * 7),
        vy: (Math.random() - 0.5) * 3,
        life: 500 + Math.random() * 400,
        type: 'portal',
        rgb,
      });
    }
  };

  /**
   * Everything the simulation causes but does not own, as this browser tab
   * carries it out: sound with its camera response (relayed to an online
   * guest), sparks, the dolly between rooms, and the local win tally and
   * wallet. The contract is documented in simulation.js.
   */
  const simFx = {
    sound: gameSound,
    particle: createParticle,
    screenChange: playScreenChange,
    recordWin,
    settleSeries,
  };

  /**
   * Reset the arena for the next game of a series, keeping the tally and stake.
   *
   * Deliberately a fresh game state rather than a partial cleanup: every game
   * of a series should open exactly like the first one, on the landing stage
   * with full health and nothing in flight, and a hand-rolled reset is how
   * something gets left behind.
   */
  const nextGameInSeries = () => {
    const prev = gameStateRef.current;
    if (!seriesLive(prev)) return;
    gameStateRef.current = createGameState({
      gameMode: prev.gameMode,
      difficulty: prev.difficulty,
      series: { ...prev.series, game: prev.series.game + 1 },
    });
    impactRef.current.shake = 0;
    impactRef.current.flash = 0;
    transitionRef.current.time = 0;
  };

  const drawPlayerBody = (ctx, x, y, color, facingRight, pose, boardMode = 'feet') => {
    const direction = facingRight ? 1 : -1;
    
    // Proper Mega Man colors
    const primaryColor = color === '#00ff41' ? '#4A90E2' : '#E74C3C'; // Blue for P1, Red for P2
    const secondaryColor = color === '#00ff41' ? '#2E5B8C' : '#A93226'; // Darker blue/red
    const lightColor = color === '#00ff41' ? '#7FB3E3' : '#F1948A'; // Lighter blue/red
    const skinColor = '#FDBCB4';
    const darkColor = '#2C3E50';
    const whiteColor = '#FFFFFF';
    
    if (pose === 'riding') {
      if (boardMode === 'hands') {
        // === PROPER MEGA MAN SHOOTING POSE ===
        
        // === HEAD ===
        // Face/skin
        ctx.fillStyle = skinColor;
        ctx.fillRect(x - 4, y - 8, 8, 6);
        
        // Helmet
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 5, y - 10, 10, 4); // Top of helmet
        ctx.fillRect(x - 4, y - 6, 8, 2); // Front of helmet
        
        // Helmet details
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 4, y - 9, 8, 1); // Helmet line
        
        // Eyes
        ctx.fillStyle = darkColor;
        if (facingRight) {
          ctx.fillRect(x + 1, y - 6, 2, 1);
          ctx.fillRect(x + 1, y - 4, 2, 1);
        } else {
          ctx.fillRect(x - 3, y - 6, 2, 1);
          ctx.fillRect(x - 3, y - 4, 2, 1);
        }
        
        // === BODY ===
        // Main body
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 4, y - 1, 8, 10);
        
        // Chest armor details
        ctx.fillStyle = lightColor;
        ctx.fillRect(x - 3, y, 6, 6);
        
        // Body lines
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 3, y + 2, 6, 1);
        ctx.fillRect(x - 2, y + 4, 4, 1);
        
        // === ARMS (Classic Mega Man shooting pose) ===
        if (facingRight) {
          // Extended shooting arm (right)
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x + 4, y + 1, 6, 3); // Upper arm extended
          ctx.fillStyle = lightColor;
          ctx.fillRect(x + 9, y + 1, 4, 3); // Forearm
          
          // Mega Buster
          ctx.fillStyle = secondaryColor;
          ctx.fillRect(x + 12, y + 1, 4, 3);
          ctx.fillStyle = darkColor;
          ctx.fillRect(x + 15, y + 2, 2, 1); // Barrel
          
          // Supporting arm (left)
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x - 6, y + 2, 3, 4);
          ctx.fillStyle = lightColor;
          ctx.fillRect(x - 7, y + 5, 3, 2);
          
        } else {
          // Extended shooting arm (left)
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x - 10, y + 1, 6, 3);
          ctx.fillStyle = lightColor;
          ctx.fillRect(x - 13, y + 1, 4, 3);
          
          // Mega Buster
          ctx.fillStyle = secondaryColor;
          ctx.fillRect(x - 16, y + 1, 4, 3);
          ctx.fillStyle = darkColor;
          ctx.fillRect(x - 17, y + 2, 2, 1);
          
          // Supporting arm (right)
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x + 3, y + 2, 3, 4);
          ctx.fillStyle = lightColor;
          ctx.fillRect(x + 4, y + 5, 3, 2);
        }
        
        // === LEGS (Wide action stance) ===
        if (facingRight) {
          // Right leg forward
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x + 1, y + 9, 3, 6); // Right thigh
          ctx.fillRect(x + 1, y + 14, 3, 5); // Right shin
          
          // Left leg back
          ctx.fillRect(x - 5, y + 10, 3, 5); // Left thigh
          ctx.fillRect(x - 5, y + 14, 3, 4); // Left shin
          
          // Boots
          ctx.fillStyle = secondaryColor;
          ctx.fillRect(x + 0, y + 18, 5, 3); // Right boot
          ctx.fillRect(x - 6, y + 17, 4, 3); // Left boot
          
        } else {
          // Left leg forward
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x - 4, y + 9, 3, 6);
          ctx.fillRect(x - 4, y + 14, 3, 5);
          
          // Right leg back
          ctx.fillRect(x + 2, y + 10, 3, 5);
          ctx.fillRect(x + 2, y + 14, 3, 4);
          
          // Boots
          ctx.fillStyle = secondaryColor;
          ctx.fillRect(x - 5, y + 18, 5, 3);
          ctx.fillRect(x + 2, y + 17, 4, 3);
        }
        
        // === HOVERBOARD AS WEAPON ===
        const boardX = x + (facingRight ? 10 : -10);
        const boardY = y + 2;
        const boardColor = color === '#00ff41' ? '#00d935' : '#ff3742';
        
        ctx.fillStyle = boardColor;
        ctx.fillRect(boardX - 8, boardY, 16, 4);
        ctx.fillStyle = color;
        ctx.fillRect(boardX - 6, boardY + 1, 12, 2);
        
      } else {
        // === FEET MODE: Riding on hoverboard ===
        
        // Head
        ctx.fillStyle = skinColor;
        ctx.fillRect(x - 4, y - 6, 8, 5);
        
        // Helmet
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 5, y - 8, 10, 3);
        ctx.fillRect(x - 4, y - 5, 8, 1);
        
        // Eyes
        ctx.fillStyle = darkColor;
        if (facingRight) {
          ctx.fillRect(x + 1, y - 4, 2, 1);
        } else {
          ctx.fillRect(x - 3, y - 4, 2, 1);
        }
        
        // Body
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 4, y, 8, 8);
        ctx.fillStyle = lightColor;
        ctx.fillRect(x - 3, y + 1, 6, 4);
        
        // Arms in riding position
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 5, y + 3, 3, 3); // Left arm
        ctx.fillRect(x + 2, y + 3, 3, 3); // Right arm
        
        // Legs crouched
        ctx.fillRect(x - 3, y + 8, 2, 4); // Left leg
        ctx.fillRect(x + 1, y + 8, 2, 4); // Right leg
        
        // Boots
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 4, y + 11, 3, 2);
        ctx.fillRect(x + 1, y + 11, 3, 2);
        
        // Hoverboard
        const boardColor = color === '#00ff41' ? '#00d935' : '#ff3742';
        ctx.fillStyle = boardColor;
        ctx.fillRect(x - 10, y + 13, 20, 4);
        ctx.fillStyle = color;
        ctx.fillRect(x - 8, y + 14, 16, 2);
      }
      
    } else {
      // === STANDING/DISMOUNTED: Action pose ===
      
      // Head
      ctx.fillStyle = skinColor;
      ctx.fillRect(x - 4, y - 6, 8, 5);
      
      // Helmet
      ctx.fillStyle = primaryColor;
      ctx.fillRect(x - 5, y - 8, 10, 3);
      ctx.fillRect(x - 4, y - 5, 8, 1);
      
      // Eyes
      ctx.fillStyle = darkColor;
      if (facingRight) {
        ctx.fillRect(x + 1, y - 4, 2, 1);
      } else {
        ctx.fillRect(x - 3, y - 4, 2, 1);
      }
      
      // Body
      ctx.fillStyle = primaryColor;
      ctx.fillRect(x - 4, y, 8, 8);
      ctx.fillStyle = lightColor;
      ctx.fillRect(x - 3, y + 1, 6, 4);
      ctx.fillStyle = secondaryColor;
      ctx.fillRect(x - 2, y + 2, 4, 1);
      
      // Arms (shooting pose)
      if (facingRight) {
        // Shooting arm extended
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x + 4, y + 2, 5, 3);
        ctx.fillStyle = lightColor;
        ctx.fillRect(x + 8, y + 2, 3, 3);
        
        // Mega Buster
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x + 10, y + 2, 3, 3);
        
        // Supporting arm
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 6, y + 3, 3, 3);
        
      } else {
        // Shooting arm extended
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 9, y + 2, 5, 3);
        ctx.fillStyle = lightColor;
        ctx.fillRect(x - 11, y + 2, 3, 3);
        
        // Mega Buster
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 13, y + 2, 3, 3);
        
        // Supporting arm
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x + 3, y + 3, 3, 3);
      }
      
      // Legs (wide stance)
      if (facingRight) {
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x + 0, y + 8, 3, 5); // Right leg forward
        ctx.fillRect(x - 4, y + 9, 3, 4); // Left leg back
        
        // Boots
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 1, y + 12, 4, 2);
        ctx.fillRect(x - 5, y + 12, 3, 2);
        
      } else {
        ctx.fillStyle = primaryColor;
        ctx.fillRect(x - 3, y + 8, 3, 5); // Left leg forward
        ctx.fillRect(x + 1, y + 9, 3, 4); // Right leg back
        
        // Boots
        ctx.fillStyle = secondaryColor;
        ctx.fillRect(x - 3, y + 12, 4, 2);
        ctx.fillRect(x + 2, y + 12, 3, 2);
      }
    }
  };

  /**
   * Pick the drawing that represents what a player is doing this frame.
   *
   * This is what makes the character read as a body rather than a decal. The
   * art is a library of distinct poses and every state the simulation can be in
   * maps onto the one that shows it, so a carve, a flip and a sprint are
   * genuinely different drawings instead of one drawing tilted three ways.
   * Gaits additionally alternate between opposite-phase frames, because a cycle
   * cannot be shown by a single instant however it is transformed.
   *
   * @param {object} player
   * @returns {string} key into GEOMETRY.
   */
  const choosePose = (player) => {
    const phase = animPhaseRef.current;

    // Spinning beats everything else, and the check has to come before the
    // mount test rather than inside it: the backflip is launched BY kicking the
    // board away, so the rider is already dismounted for the whole spin. Nested
    // under `onBoard` this never fires, and the flip plays out as a standing
    // figure rotating stiffly end over end.
    if (player.rotation !== 0) return 'tuck';

    // A handling move owns the body until it lands. Both the draw and the stow
    // use the same drawing — the rider hauling the board across their chest —
    // because the two moves are the same motion run in opposite directions.
    if (player.transition === 'draw' || player.transition === 'stow') return 'draw';
    if (player.transition === 'mount') return 'mount';

    if (player.onBoard) {
      if (player.boardMode === 'hands') {
        // Aiming is a full movement state rather than one frozen drawing: the
        // rider walks, jumps and takes the recoil without ever coming off aim.
        const since = BEAM_RECHARGE_TIME - player.beamRecharge;
        if (since >= 0 && since < RECOIL_MS) return 'recoil';
        if (Math.abs(player.vy) > AIRBORNE_VY) return 'gunjump';
        if (Math.abs(player.vx) >= MOVING_VX) {
          const gait = CYCLES.gunWalk;
          return gait.frames[Math.floor(phase / gait.hold) % gait.frames.length];
        }
        return 'shoot';
      }
      // A hard carve drops the rider into the low stance.
      return Math.abs(player.vx) > BOARD_SPEED * 0.6 ? 'carve' : 'ride';
    }

    // Hand-to-hand owns the body whenever it has something to say — being on
    // the floor, reeling, swinging or holding a crouch all beat locomotion.
    const fighting = meleePose(player);
    if (fighting) return fighting;

    if (Math.abs(player.vy) > AIRBORNE_VY) return 'jump';
    const speed = Math.abs(player.vx);
    if (speed < MOVING_VX) return 'idle';
    const gait = speed > PLAYER_SPEED * 0.5 ? CYCLES.run : CYCLES.walk;
    return gait.frames[Math.floor(phase / gait.hold) % gait.frames.length];
  };

  /**
   * Derive the continuous layer of motion that plays over the chosen pose.
   *
   * The poses are stills. Leaning into a turn, pitching with climb and dive,
   * breathing on the hover and dropping on each footfall is the rest of what
   * reads as alive, and all of it falls out of vx/vy, which are already synced
   * for online play and so need no state of their own.
   *
   * Everything returned is in the rider's LOCAL space, where +x is the
   * direction the rider faces. `drive` rather than raw vx is what the lean is
   * built from, so a rider moving backwards leans back — mirroring alone would
   * otherwise tilt a left-running rider the wrong way.
   *
   * @param {object} player
   * @param {'foot'|'board'|'hands'} mode
   * @returns {{tilt: number, bob: number, squash: number, lean: number,
   *   climb: number}} radians and pixels.
   */
  const getRiderMotion = (player, mode) => {
    const facing = player.facingRight ? 1 : -1;
    // Phase runs on the render clock: purely cosmetic, so it needs no sync and
    // no simulation state of its own.
    const phase = animPhaseRef.current;
    const clamp = (v) => Math.max(-1, Math.min(1, v));

    // Hands mode is walked, not flown: the rider is on their feet with the
    // board in their arms, so it shares the on-foot treatment rather than the
    // board's carve-and-pitch — just braced, because they are holding an aim.
    if (mode === 'foot' || mode === 'hands') {
      const braced = mode === 'hands';
      const drive = clamp((player.vx * facing) / PLAYER_SPEED);
      const airborne = Math.abs(player.vy) > AIRBORNE_VY;
      const running = Math.abs(player.vx) >= MOVING_VX && !airborne;

      // A dive is drawn along the line it is travelling. The kick sprite alone
      // reads as a horizontal flying kick, so worn on a descending trajectory
      // it looks like a rider gliding rather than dropping on someone. Pitching
      // the whole body to match the velocity is the difference between the two,
      // and it costs no state: vx and vy are already synced, so the guest draws
      // the same dive the host does.
      if (isDiving(player)) {
        return {
          tilt: diveAngle(player), bob: 0, squash: 1, lean: 1, climb: 0,
        };
      }

      return {
        tilt: drive * (braced ? 0.05 : 0.12) + (airborne ? 0.08 : 0),
        // Footfall bob: the body drops as each foot lands, which is what sells
        // a two-frame gait as a stride rather than a flicker between drawings.
        bob: running
          ? Math.abs(Math.sin(phase * (braced ? 0.13 : 0.16))) * (braced ? -0.8 : -1.3)
          : 0,
        squash: 1,
        lean: drive,
        climb: 0,
      };
    }

    const drive = clamp((player.vx * facing) / BOARD_SPEED);
    // Climb is world-vertical and the sprite is never mirrored vertically, so
    // unlike the lean it must not be signed by facing.
    // Normalised by the CLIMB cap, not the skating cap, so a full-speed climb
    // still reaches the full pitch of the pose now that the two differ.
    const climb = clamp(player.vy / BOARD_CLIMB_SPEED);
    // Hands mode is a braced firing stance — much less body movement.
    const looseness = mode === 'hands' ? 0.35 : 1;

    return {
      tilt: (drive * 0.18 + climb * 0.10) * looseness,
      // Idle hover breathes; the bob washes out as speed builds.
      bob: Math.sin(phase * 0.09) * 1.6 * (1 - Math.abs(drive)) * looseness,
      // Compress slightly under hard climb, extend on the drop.
      squash: 1 - climb * 0.06 * looseness,
      lean: drive,
      climb,
    };
  };

  /**
   * How far the current handling move has run: 0 as it starts, 1 once it lands.
   * Returns 1 when nothing is in progress, so callers can blend against it
   * unconditionally instead of branching first.
   *
   * @param {object} player
   * @returns {number} progress in 0..1.
   */
  const transitionProgress = (player) => {
    if (!player.transition) return 1;
    const total = player.transition === 'draw' ? DRAW_TIME
      : player.transition === 'stow' ? STOW_TIME : MOUNT_TIME;
    return Math.max(0, Math.min(1, 1 - player.transitionTime / total));
  };

  /** Smoothstep, so the board eases out of one hand-off and into the next. */
  const easeInOut = (t) => t * t * (3 - 2 * t);

  /**
   * Work out where the board sits this frame, in the rider's local space: the
   * origin is the player's feet, +x is forward and +y is down.
   *
   * Both the sprite renderer and the thruster glow read this, so the light
   * under the deck always tracks the deck itself — including when the board is
   * up in the rider's hands rather than beneath their boots.
   *
   * @param {object} player
   * @param {string} pose Key into GEOMETRY, for the rider's foot position.
   * @param {object} motion From getRiderMotion.
   * @returns {{x: number, y: number, rot: number}} deck centre and pitch.
   */
  const getBoardPlacement = (player, pose, motion) => {
    const geom = GEOMETRY[pose];
    // The board's two resting places, in the rider's local space.
    const deck = {
      // Under the boots, not under the sprite's middle. A pose that throws an
      // arm forward has its centre of mass well ahead of where it is standing,
      // and hanging the board off that slides it out from under the rider.
      x: geom.footX - geom.centerX,
      y: SPRITE_BOTTOM - GEOMETRY.board.h / 2,
      rot: motion.climb * 0.16 + motion.lean * 0.06,
    };
    // Aimed as a cannon: the nose lands exactly on the muzzle the beam is
    // spawned from, so the shot leaves the barrel it appears to leave.
    const held = { x: HELD_BOARD.x, y: HELD_BOARD.y, rot: 0 };

    if (player.transition) {
      const t = easeInOut(transitionProgress(player));

      if (player.transition === 'mount') {
        // Swings up from below and settles under the boots as the rider drops
        // onto it, so mounting is the board arriving rather than appearing.
        return {
          x: deck.x,
          y: deck.y + (1 - t) * MOUNT_RISE,
          rot: deck.rot + (1 - t) * MOUNT_TILT,
        };
      }

      // Draw and stow are the same journey in opposite directions. This is the
      // payoff of the board being its own sprite: it genuinely travels between
      // the boots and the hands instead of cutting from one to the other.
      const from = player.transition === 'draw' ? deck : held;
      const to = player.transition === 'draw' ? held : deck;
      return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
        // Bowed through the middle of the move so the board sweeps up on an arc
        // rather than sliding along a straight line between two points.
        rot: from.rot + (to.rot - from.rot) * t - Math.sin(t * Math.PI) * SWING_ARC,
      };
    }

    if (player.boardMode === 'hands') {
      // The recoil drawing snaps the arms back toward the chest; the board has
      // to come with them or it hangs in front of hands that have let go.
      if (pose === 'recoil') {
        return {
          x: held.x + RECOIL_KICK.x,
          y: held.y + RECOIL_KICK.y,
          rot: RECOIL_KICK.rot,
        };
      }
      return held;
    }
    return deck;
  };

  /**
   * Carry a rider's local space into the world: their position, the backflip
   * spin, which way they face, and the squash of a hard climb. Local space has
   * its origin at the feet, +x forward and +y down — the space
   * getBoardPlacement answers in.
   *
   * ONE definition, used by the sprite and by everything drawn relative to it.
   * The thruster glow used to rebuild this by hand from the position and the
   * facing alone, so through a backflip — which a rider can remount partway
   * into — the board spun with the body while its glow sat level under the
   * boots, where the board was only going to be once the flip was over.
   *
   * @param {object} t Anything with translate / rotate / scale: a canvas
   *   context, or matrixOps() over a DOMMatrix.
   * @param {object} player
   * @param {object} motion From getRiderMotion.
   */
  const applyRiderSpace = (t, player, motion) => {
    t.translate(player.x, player.y);
    // A backflip spins rider and board together about the body's middle.
    if (player.rotation !== 0) {
      t.translate(0, FLIP_PIVOT);
      t.rotate(player.rotation);
      t.translate(0, -FLIP_PIVOT);
    }
    if (!player.facingRight) t.scale(-1, 1);
    if (motion.squash !== 1) {
      t.translate(0, FLIP_PIVOT);
      t.scale(1 / motion.squash, motion.squash);
      t.translate(0, -FLIP_PIVOT);
    }
  };

  /**
   * A DOMMatrix behind the canvas transform interface, so a world position can
   * be worked out with exactly the steps that draw it.
   *
   * @param {DOMMatrix} m Mutated in place.
   */
  const matrixOps = (m) => ({
    translate: (x, y) => m.translateSelf(x, y),
    rotate: (r) => m.rotateSelf((r * 180) / Math.PI), // DOMMatrix takes degrees
    scale: (x, y) => m.scaleSelf(x, y),
  });

  /**
   * Draw a player using the sprite art if it's loaded.
   *
   * The rider and the board are two sprites composited each frame rather than
   * one baked drawing. That is what lets the deck carve under a body that is
   * leaning its own way, stay locked to the boots through a backflip, and swing
   * up into the hands to be aimed — none of which a single image can do.
   *
   * A team-coloured rim light is drawn behind the whole assembly. The art is
   * almost entirely dark navy, so without the outline green and red riders are
   * indistinguishable against a night cityscape.
   *
   * @returns {boolean} true if drawn; false when the art isn't ready yet
   *   (caller falls back to the procedural pixel figure).
   */
  const drawPlayerSprite = (ctx, player, playerId) => {
    const pose = choosePose(player);
    const rider = getSprite(playerId, pose);
    const deck = getSprite(playerId, 'board');
    if (!rider || !deck) return false;

    const mode = player.onBoard
      ? (player.boardMode === 'hands' ? 'hands' : 'board')
      : 'foot';
    const motion = getRiderMotion(player, mode);
    const geom = GEOMETRY[pose];
    const board = GEOMETRY.board;
    // A dismounted rider's board is loose in the world, drawn by drawLooseBoard.
    const placement = player.onBoard
      ? getBoardPlacement(player, pose, motion)
      : null;

    // Standing on the deck raises the body by the board's thickness, less the
    // few pixels the boots sink into it so the two read as one object.
    //
    // Ramped with the handling move rather than applied the instant the mode
    // flips: the rider rises as the board arrives underfoot and sinks as it
    // leaves, instead of popping five pixels up or down on a single frame.
    const lift = board.h - DECK_INSET;
    const settle = player.transition === 'draw'
      ? 1 - transitionProgress(player)
      : (player.transition === 'stow' || player.transition === 'mount')
        ? transitionProgress(player)
        : (mode === 'board' ? 1 : 0);
    const riderBottom = SPRITE_BOTTOM - lift * settle + motion.bob;

    // How far through the death this rider is, 0 while alive. Derived from the
    // already-synced vaporizing timer, so the collapse needs no state of its
    // own and the online guest plays exactly the host's version of it.
    const dying = player.vaporizing > 0
      ? 1 - player.vaporizing / VAPORIZE_TIME
      : 0;
    // Distinct per rider, so the two never glitch on the same frame pattern.
    const seed = playerId === 'player1' ? 0x51d0 : 0xd407;

    /**
     * Lay one image down. While a rider is dying every piece of them — body,
     * deck, rim ghosts — goes through the band shredder instead, which is what
     * keeps the collapse composited in the right order without a second
     * rendering path.
     */
    const blit = dying > 0
      ? (image, x, y) => drawHoloBands(ctx, image, x, y, dying, seed)
      : (image, x, y) => ctx.drawImage(image, x, y);

    /** Paint the deck at its placement, centred on its own middle. */
    const paintDeck = (image) => {
      ctx.save();
      ctx.translate(placement.x, placement.y);
      if (placement.rot) ctx.rotate(placement.rot);
      blit(image, -board.w / 2, -board.h / 2);
      ctx.restore();
    };

    /** Paint the rider, hinged at the hips so a lean pivots off the legs. */
    const paintRider = (image) => {
      ctx.save();
      if (motion.tilt) {
        const pivotY = riderBottom - geom.h * HIP_HEIGHT_FRAC;
        ctx.translate(0, pivotY);
        ctx.rotate(motion.tilt);
        ctx.translate(0, -pivotY);
      }
      blit(image, -geom.centerX, riderBottom - geom.h);
      ctx.restore();
    };

    // Whether the board is in front of the body or behind it, decided by where
    // the board actually is rather than by which mode is selected. Keying off
    // the mode breaks during a handling move: at the start of a stow the board
    // is still up at chest height, and drawing it behind the torso there makes
    // it vanish. Above the hips it is in the rider's hands and occludes them;
    // below, it is underfoot and the boots occlude it. Mid-draw it crosses over
    // exactly when it passes the waist, which is what really happens.
    const boardInFront =
      placement && placement.y < SPRITE_BOTTOM - geom.h * HIP_HEIGHT_FRAC;

    /**
     * Paint the whole assembly back to front from one image set, so the rim
     * pass and the art pass lay out identically.
     */
    const paint = (riderImage, deckImage) => {
      if (placement && !boardInFront) paintDeck(deckImage);
      paintRider(riderImage);
      if (placement && boardInFront) paintDeck(deckImage);
    };

    ctx.save();
    applyRiderSpace(ctx, player, motion);

    // Spawn protection reads as a flicker over the whole figure.
    if (player.invuln > 0) {
      ctx.globalAlpha = 0.35 + 0.4 * Math.abs(Math.sin(player.invuln / 60));
    }

    const riderRim = getSilhouette(playerId, pose);
    const deckRim = getSilhouette(playerId, 'board');

    if (dying > 0) {
      // The whole figure dims as it goes, but only late: fading from the first
      // frame would hide the glitch detail while it is at its most violent.
      ctx.globalAlpha *= holoAlpha(dying);

      // Colour ghosts stand in for the rim light here. A rim is an outline
      // around something solid, and there is nothing solid left to outline —
      // two copies pulling apart sideways says "signal failing" instead.
      const split = chromaSplit(dying);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha *= 0.5;
      [[-split, HOLO_CYAN], [split, teamColor(playerId)]].forEach(([ox, color]) => {
        ctx.save();
        ctx.translate(ox, 0);
        paint(getTinted(rider, color), getTinted(deck, color));
        ctx.restore();
      });
      ctx.restore();

      paint(rider, deck);

      // Readout line last, over the top of everything it passes through.
      drawHoloScan(
        ctx, getTinted(rider, HOLO_CYAN),
        -geom.centerX, riderBottom - geom.h, dying
      );
      ctx.restore();
      return true;
    }

    // Rim pass first, so the outline sits behind every piece rather than
    // between the rider and the board.
    if (riderRim && deckRim) {
      ctx.save();
      ctx.globalAlpha *= RIM_ALPHA;
      RIM_OFFSETS.forEach(([ox, oy]) => {
        ctx.save();
        ctx.translate(ox, oy);
        paint(riderRim, deckRim);
        ctx.restore();
      });
      ctx.restore();
    }

    paint(rider, deck);
    ctx.restore();
    return true;
  };

  /**
   * Draw the loose hoverboard: the same deck sprite the rider stands on,
   * tumbling in its direction of travel with a motion trail and thruster glow.
   *
   * Using the ridden board's own art here is the point of having split the
   * board out of the character sprite. The board mid-kick is the most
   * eye-catching object in a fight, and when the thrown board was drawn from
   * scratch it was visibly a different object from the one that had just been
   * underfoot.
   */
  const drawLooseBoard = (ctx, board, playerId, dead = false) => {
    const deck = getSprite(playerId, 'board');
    // A board killed by an EMP has lost its team colour along with its drive —
    // the glow IS the machine running, so a dead one is a grey object on the
    // deck, and the only light left on it is the fault arcing across it.
    const glowRgb = dead ? '150, 170, 190' : (playerId === 'player1' ? '0, 255, 65' : '255, 71, 87');
    const speed = Math.hypot(board.vx, board.vy);
    const geom = GEOMETRY.board;

    if (!deck) return;

    /** Stamp the deck centred on the origin of the current transform. */
    const stamp = () => ctx.drawImage(deck, -geom.w / 2, -geom.h / 2);

    // Motion trail: ghosts of the board's recent path, fading backward.
    if (speed > 3) {
      for (let i = 1; i <= 3; i++) {
        const t = i / 4;
        ctx.save();
        ctx.globalAlpha = 0.22 * (1 - t);
        ctx.translate(board.x - board.vx * i * 0.8, board.y - board.vy * i * 0.8);
        ctx.rotate((board.spin || 0) - board.vx * 0.02 * i);
        stamp();
        ctx.restore();
      }
    }

    ctx.save();
    ctx.translate(board.x, board.y);

    // Thruster wash and nozzle plumes stay upright rather than tumbling with
    // the deck, so the drive reads as pushing against the world.
    const glow = ctx.createLinearGradient(0, 0, 0, 18);
    glow.addColorStop(0, `rgba(${glowRgb}, ${Math.min(0.55, 0.18 + speed * 0.03)})`);
    glow.addColorStop(1, `rgba(${glowRgb}, 0)`);
    ctx.fillStyle = glow;
    ctx.fillRect(-BOARD_HALF_WIDTH, 2, BOARD_HALF_WIDTH * 2, 18);

    const w = BOARD_HALF_WIDTH;
    if (dead) {
      // Nothing is running. A fault arcs across the deck instead — short white
      // hairlines snapping between the nozzles, on their own irregular beat so
      // it reads as a fault rather than as an animation.
      const beat = Math.sin(animPhaseRef.current * 0.9) * Math.sin(animPhaseRef.current * 0.31);
      if (beat > 0.45) {
        ctx.strokeStyle = 'rgba(210, 235, 255, 0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i < 3; i++) {
          const t = (i + 1) / 4;
          ctx.moveTo(-w + w * 2 * t, -3);
          ctx.lineTo(-w + w * 2 * t + (i % 2 ? 4 : -4), 4);
        }
        ctx.stroke();
      }
    } else {
      // Twin thruster nozzles, flickering out of phase so the drive reads live.
      [-w * 0.55, w * 0.55].forEach((nx, i) => {
        const flicker = 0.6 + 0.4 * Math.sin(animPhaseRef.current * 0.5 + i * 2.1);
        const plume = ctx.createRadialGradient(nx, 5, 0, nx, 5, 9);
        plume.addColorStop(0, `rgba(255, 255, 255, ${0.55 * flicker})`);
        plume.addColorStop(0.45, `rgba(${glowRgb}, ${0.5 * flicker})`);
        plume.addColorStop(1, `rgba(${glowRgb}, 0)`);
        ctx.fillStyle = plume;
        ctx.fillRect(nx - 9, -4, 18, 18);
      });
    }

    ctx.rotate(board.spin || 0);
    stamp();
    ctx.restore();
  };

  /**
   * Thruster wash under a rider's board.
   *
   * Drawn in the DECK'S OWN FRAME — the rider's space (applyRiderSpace), then
   * the placement the sprite draws the deck at — so the wash leaves the
   * board's underside wherever the board is and however it is turned: up in
   * the rider's hands when it is being aimed, tilted as it swings into place,
   * and round with the body through a backflip.
   */
  const drawRiderThruster = (ctx, player, playerId) => {
    const glowRgb = playerId === 'player1' ? '0, 255, 65' : '255, 71, 87';
    const mode = player.boardMode === 'hands' ? 'hands' : 'board';
    const pose = choosePose(player);
    const motion = getRiderMotion(player, mode);
    const placement = getBoardPlacement(player, pose, motion);
    const underside = GEOMETRY.board.h / 2;
    const intensity = Math.min(1, 0.35 + Math.hypot(player.vx, player.vy) / 12);

    /** Carry a transform from the world to the deck's centre and pitch. */
    const toDeck = (t) => {
      applyRiderSpace(t, player, motion);
      t.translate(placement.x, placement.y);
      if (placement.rot) t.rotate(placement.rot);
    };

    ctx.save();
    toDeck(ctx);
    const wash = ctx.createLinearGradient(0, underside, 0, underside + 22);
    wash.addColorStop(0, `rgba(${glowRgb}, ${0.4 * intensity})`);
    wash.addColorStop(1, `rgba(${glowRgb}, 0)`);
    ctx.fillStyle = wash;
    ctx.fillRect(-20, underside, 40, 22);
    ctx.restore();

    // Where the underside actually is in the world, for the light it throws
    // on the roof below.
    const m = new DOMMatrix();
    toDeck(matrixOps(m));
    const deck = m.transformPoint({ x: 0, y: underside });
    const deckX = deck.x;

    // Pool of light where the drive washes the deck below: brightest with
    // the board right on it, spreading and fading as it climbs. A grounded
    // board used to cast nothing at all, which is exactly when it should
    // cast the most — this pool, and its twin in the deck's reflection, are
    // what sit the rider ON the roof.
    const drop = Math.max(0, Math.min(GROUND_Y - deck.y, 140));
    const pool = 1 - drop / 140;
    if (pool > 0.02) {
      const r = 30 + drop * 0.35;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.translate(deckX, GROUND_Y);
      ctx.scale(1, 0.18);
      const light = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      light.addColorStop(0, `rgba(${glowRgb}, ${0.34 * pool * intensity})`);
      light.addColorStop(1, `rgba(${glowRgb}, 0)`);
      ctx.fillStyle = light;
      ctx.fillRect(-r, -r, r * 2, r * 2);
      ctx.restore();
    }
  };

  /**
   * Draw the arena: background art, territory wash, floor and goal portals.
   */
  /**
   * Paint the backdrop across the whole FRAME, not just the play area.
   *
   * Drawn in frame coordinates before the world transform is applied, because
   * the whole point of the frame is that it is bigger than the play area — the
   * city has to reach the edges of the device, and the deck has to run the full
   * width or the rooftop appears to stop in mid-air either side of the fight.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} screen May be FRACTIONAL during the dolly — the parallax
   *   pans continuously between the two rooms' city positions, which is what
   *   sells the travel as movement through one place.
   * @param {object} view Frame size and play-area offset (see viewport.js).
   */
  const drawBackdrop = (ctx, screen, view, cam) => {
    const players = gameStateRef.current.players;
    // Everything here is in frame coordinates, so the camera has to be applied
    // by hand — the backdrop is painted before the world transform, because it
    // has to reach the edges of a frame the play area does not fill.
    const mid = worldToFrame(
      cam, view.width, view.height,
      (players.player1.x + players.player2.x) / 2, 0
    );
    const ground = worldToFrame(cam, view.width, view.height, 0, GROUND_Y);
    const horizon = worldToFrame(
      cam, view.width, view.height, 0, sceneEyeRef.current
    );
    const midX = mid.x;
    const groundY = ground.y;
    const horizonY = horizon.y;

    // A distant skyline sits AT the horizon — it is far enough away that
    // perspective has already collapsed it there, which is why a city on the
    // horizon barely moves however the viewer does. So the city is anchored
    // to the horizon rather than to the deck under the fighters' feet.
    //
    // During the fight the two are the same anchor: the deck lies exactly
    // GROUND_Y - HORIZON_Y under the horizon, which is what this reproduces,
    // so gameplay is pixel-identical. They part company only when the eye
    // leaves the deck — the victory camera craning above the roof — and
    // there the difference is the whole point: anchored to the deck the city
    // drops out of frame with it, and the champion celebrates against an
    // empty sky.
    const cityGroundY = horizonY + (GROUND_Y - HORIZON_Y) * cam.zoom;

    // The layered city. It replaced four flat per-screen photographs, which
    // are gone rather than kept as a fallback: they were 636KB that every
    // player downloaded to cover a case the flat fill below already covers.
    // Layer sizes are given in play-area units, so they scale with the camera
    // and the city zooms with the fight rather than sliding underneath it.
    if (!drawParallax(ctx, screen, midX, view.width, view.height, cityGroundY,
                      CANVAS_HEIGHT * cam.zoom, horizonY,
                      performance.now() / 1000)) {
      // Flat fill for the frames before the layers arrive, or if they fail.
      ctx.fillStyle = '#0b0f1e';
      ctx.fillRect(0, 0, view.width, view.height);
    }
    // The tint is a per-screen lookup, so it takes the nearest whole room.
    paintTerritoryTint(ctx, Math.round(screen));

    // The city art's own bottom edge. Through the fight the deck is drawn
    // over it, so it is never seen; once the victory camera cranes above the
    // deck that edge is left hanging in mid-air — a hard horizontal cut
    // across every layer with the bare tint showing through underneath.
    // Fade the city down into the night there instead, so the buildings run
    // out of light rather than running out of image. The gradient clears the
    // lowest layer's base (nearLayer sits 0.09 of a play-height down) and
    // everything below it is solid night. Scaled by the crane blend, so
    // through the fight this draws nothing at all.
    const crane = victoryEyeRef.current;
    if (crane > 0.01) {
      const playH = CANVAS_HEIGHT * cam.zoom;
      const fadeTop = cityGroundY - playH * 0.10;
      const fadeEnd = cityGroundY + playH * 0.13;
      const fade = ctx.createLinearGradient(0, fadeTop, 0, fadeEnd);
      fade.addColorStop(0, 'rgba(6, 8, 16, 0)');
      fade.addColorStop(1, `rgba(6, 8, 16, ${crane})`);
      ctx.fillStyle = fade;
      ctx.fillRect(0, fadeTop, view.width, Math.max(0, fadeEnd - fadeTop));
      ctx.fillStyle = `rgba(6, 8, 16, ${crane})`;
      ctx.fillRect(0, fadeEnd, view.width, Math.max(0, view.height - fadeEnd));
    }
    // The layered city has no floor of its own, so the deck is drawn rather
    // than merely tinted.
    // Plating seams are fixed to the roof: anchored at world x=0 and spaced
    // in world units, so the camera slides over them.
    drawRooftop(ctx, groundY, view.width, view.height, horizonY, {
      originX: worldToFrame(cam, view.width, view.height, 0, GROUND_Y).x,
      step: DECK_SEAM_SPACING * cam.zoom,
    });
  };

  /**
   * The arena's rear structure. The goal-exit identity (glow, label) lives on
   * the goal portals themselves in drawDoorPortal — it used to be a separate
   * rectangle painted in flat world coordinates, which could not follow the
   * perspective-projected portal and sat visibly bigger than and offset from
   * the door it was supposed to be.
   */
  const drawArena = (ctx, screen, offsetX = 0,
    vpX = screenWidth(screen) / 2, opts = {}) => {
    drawCellBehind(ctx, screen, offsetX, vpX, opts);
  };

  /**
   * Geometry of the containment cell, in one-point perspective.
   *
   * The fight plane sits in the MIDDLE of the cell's depth rather than on its
   * front face, so there is glass behind the fighters and glass in front of
   * them. That is the difference between a box drawn around the arena and an
   * arena that is inside a box — with everything on one plane the perspective
   * lines read as decoration stuck to the screen.
   *
   * Depth is expressed as a fraction: 0 is the plane the fighters stand on,
   * negative recedes toward the vanishing point, positive advances toward the
   * camera.
   *
   * @param {number} screen
   * @param {number} [offsetX] World x of this room's origin — non-zero when
   *   the room being drawn is the dolly's trailing room, one arena over.
   * @param {number} [vpXWorld] The vanishing point, in world x — the camera's
   *   centre (a vanishing point belongs to the viewer, not the room). During
   *   the dolly BOTH rooms project through this one point, which is what
   *   keeps the corridor's perspective accurate while the camera travels.
   * @returns {object} faces, projection helpers, and which side doors are
   *   goal doors on this screen.
   */
  const cellGeometry = (screen, offsetX = 0,
    vpXWorld = screenWidth(screen) / 2) => {
    const top = CEILING_Y + BODY_TOP - 22;
    const floor = GROUND_Y;
    const leftWall = offsetX - ARENA_MARGIN;
    // The room's own width. Screens differ, so two adjacent cells are
    // different sizes — which is exactly what should be visible through the
    // doorway during a screen change.
    const rightWall = offsetX + screenWidth(screen) + ARENA_MARGIN;

    // The vanishing point sits ON the horizon, because that is what a horizon
    // is: the height the viewer's eye is at. Anything else and the cell is
    // drawn from a different eye level than the city behind it, which is the
    // one perspective error a viewer notices without being able to name.
    //
    // HORIZON_Y through the fight; the victory shot raises it to the
    // camera's own height so the roof is met level instead of from below.
    const vpX = vpXWorld;
    const vpY = sceneEyeRef.current;

    // Project one world coordinate to its on-screen position at a depth.
    const xAt = (x, depth) => x + (vpX - x) * -depth;
    const yAt = (y, depth) => y + (vpY - y) * -depth;

    /** The cell's cross-section at a given depth. */
    const faceAt = (depth) => ({
      l: xAt(leftWall, depth),
      r: xAt(rightWall, depth),
      t: yAt(top, depth),
      b: yAt(floor, depth),
    });

    return {
      back: faceAt(-0.34),
      mid: faceAt(0),
      front: faceAt(0.2),
      faceAt,
      xAt,
      yAt,
      leftWall,
      rightWall,
      goalLeft: screen === 0,
      goalRight: screen === SCREEN_COUNT - 1,
    };
  };

  /**
   * The portal in one side wall, projected to screen space.
   *
   * Portals straddle the fight plane (depth 0) and float at mid-height —
   * the band between CELL_DOOR_TOP_Y and CELL_DOOR_BOTTOM_Y that the
   * advancement gate enforces. The drawn portal IS the rule the physics
   * applies. Goal portals on the end screens are wider, but obey the same
   * band as every other portal.
   *
   * @param {object} g cellGeometry result.
   * @param {'left'|'right'} side
   * @returns {object} near/far jamb sections, and whether this is a goal.
   */
  const cellDoor = (g, side) => {
    const goal = side === 'left' ? g.goalLeft : g.goalRight;
    const wallX = side === 'left' ? g.leftWall : g.rightWall;
    const dNear = goal ? 0.1 : 0.04; // toward the camera
    const dFar = goal ? -0.3 : -0.24; // toward the vanishing point
    const section = (d) => ({
      x: g.xAt(wallX, d),
      t: g.yAt(CELL_DOOR_TOP_Y, d),
      b: g.yAt(CELL_DOOR_BOTTOM_Y, d),
      deck: g.yAt(GROUND_Y, d), // where the wall below the portal meets it
    });
    return { near: section(dNear), far: section(dFar), goal, side };
  };

  /**
   * Trace a portal outline: a closed quad with all four corners rounded —
   * a floating aperture touches nothing, so no edge stays square. Corners
   * are given as [x, y] in perimeter order (near-bottom, near-top, far-top,
   * far-bottom), which also holds for every inward-lerped rib of it.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number[]} nb Near-bottom corner.
   * @param {number[]} nt Near-top corner.
   * @param {number[]} ft Far-top corner.
   * @param {number[]} fb Far-bottom corner.
   * @param {number} r Corner radius.
   */
  const doorwayPath = (ctx, nb, nt, ft, fb, r) => {
    const pts = [nb, nt, ft, fb];
    const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    ctx.beginPath();
    const start = mid(fb, nb);
    ctx.moveTo(start[0], start[1]);
    for (let i = 0; i < 4; i++) {
      const corner = pts[i];
      const m = mid(corner, pts[(i + 1) % 4]);
      ctx.arcTo(corner[0], corner[1], m[0], m[1], r);
    }
    ctx.closePath();
  };

  /**
   * The glitch portal filling a doorway: a translucent void, corridor ribs
   * receding toward a point beyond the wall, an intermittent slice glitch,
   * and a framed mouth with chromatic fringing. Goal doors carry the exit
   * colour. Purely a function of time and geometry — no state, nothing for
   * sync.js to carry.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} g cellGeometry result.
   * @param {'left'|'right'} side
   * @param {number} now Seconds, for the animation.
   */
  const drawDoorPortal = (ctx, g, side, now) => {
    const { near, far, goal } = cellDoor(g, side);

    // Every door belongs to someone: green advances right and red advances
    // left, so the right-hand door is always green's way and the left-hand
    // door red's. Each carries its owner's colour at all times — dimly while
    // it is shut to them, fully and pulsing once they hold advancement — so
    // the gate says whose it is AND whether it is open, and the direction of
    // the push is legible on every screen, not just the two goal screens.
    // Derived from advancingPlayer, which sync.js already carries.
    const game = gameStateRef.current;
    const advancing = game.advancementUnlocked ? game.advancingPlayer : null;
    const active =
      (advancing === 'player1' && side === 'right') ||
      (advancing === 'player2' && side === 'left');
    const accent = side === 'left' ? '255, 71, 87' : '0, 255, 65';
    const strength = active ? 1 : goal ? 0.85 : 0.5;
    const pulse = active ? 0.8 + 0.2 * Math.sin(now * 6) : 1;
    const outer = [
      [near.x, near.b], [near.x, near.t], [far.x, far.t], [far.x, far.b],
    ];

    // The void: darker than the glass around it but translucent, so the city
    // still ghosts through — which is what makes it read as a passage rather
    // than a painted panel.
    doorwayPath(ctx, ...outer, 10);
    ctx.closePath();
    ctx.fillStyle = 'rgba(5, 9, 20, 0.5)';
    ctx.fill();
    // Lit from within by its owner's colour: brightest when open to them,
    // a quieter version at a goal, a trace otherwise.
    doorwayPath(ctx, ...outer, 10);
    ctx.closePath();
    ctx.fillStyle = `rgba(${accent}, ${(active ? 0.1 : goal ? 0.06 : 0.03) * pulse})`;
    ctx.fill();

    // Corridor ribs: the door shape repeated, shrinking toward a focus point
    // past the wall — the entryway of a corridor that is not really there.
    const focus = {
      x: far.x + (near.x - far.x) * 0.42,
      y: far.t * 0.35 + far.b * 0.65,
    };
    const flicker = 0.75 + 0.25 * Math.sin(now * 9 + (side === 'left' ? 0 : 2));
    for (let i = 1; i <= 3; i++) {
      const k = i * 0.22;
      const pts = outer.map(([x, y]) => [
        x + (focus.x - x) * k, y + (focus.y - y) * k,
      ]);
      doorwayPath(ctx, ...pts, Math.max(2, 10 * (1 - k)));
      ctx.strokeStyle = `rgba(${accent}, ${(0.3 - i * 0.07) * flicker * strength})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Slice glitch: streaks that jump sideways, gated so they are
    // intermittent — a constant glitch would just read as texture.
    //
    // Each streak lies IN the wall: it joins the two jambs at the same
    // fraction of their height, which is a line of constant world height
    // projected through the cell's perspective (screen height is linear in
    // world height at a fixed depth). A screen-horizontal bar cut across the
    // receding door at the wrong angle. It tapers the same way — thickness
    // scales with each jamb's projected height — and the glitch jump slides
    // it along its own line, so a jumped streak stays on the wall.
    ctx.save();
    doorwayPath(ctx, ...outer, 10);
    ctx.closePath();
    ctx.clip();
    for (let i = 0; i < 3; i++) {
      const gate = Math.sin(now * 1.9 + i * 2.1 + (side === 'left' ? 0 : 3));
      if (gate < 0.45) continue;
      const f = (i * 0.31 + now * 0.13) % 1;
      const yNear = near.t + (near.b - near.t) * f;
      const yFar = far.t + (far.b - far.t) * f;
      const halfNear = (near.b - near.t) * 0.006;
      const halfFar = (far.b - far.t) * 0.006;
      const len = Math.hypot(far.x - near.x, yFar - yNear) || 1;
      const shift = Math.sin(now * 23 + i * 5) * 4;
      const sx = ((far.x - near.x) / len) * shift;
      const sy = ((yFar - yNear) / len) * shift;
      ctx.beginPath();
      ctx.moveTo(near.x + sx, yNear - halfNear + sy);
      ctx.lineTo(far.x + sx, yFar - halfFar + sy);
      ctx.lineTo(far.x + sx, yFar + halfFar + sy);
      ctx.lineTo(near.x + sx, yNear + halfNear + sy);
      ctx.closePath();
      ctx.fillStyle = `rgba(${accent}, 0.22)`;
      ctx.fill();
    }
    ctx.restore();

    // The frame: dark structural jamb, chromatic fringe, emissive core.
    doorwayPath(ctx, ...outer, 10);
    ctx.strokeStyle = 'rgba(20, 34, 58, 0.9)';
    ctx.lineWidth = goal ? 7 : 5;
    ctx.stroke();
    [[-1.2, '255, 90, 130'], [1.2, '90, 255, 255']].forEach(([dx, rgb]) => {
      ctx.save();
      ctx.translate(dx, 0);
      doorwayPath(ctx, ...outer, 10);
      ctx.strokeStyle = `rgba(${rgb}, ${0.14 + 0.08 * flicker})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    });
    if (active || goal) {
      // A wide soft stroke under the core line — the halo. This IS the goal
      // glow now: drawn from the same projected path as the door, it cannot
      // sit bigger than or offset from the portal the way the old flat
      // world-space rectangle did.
      doorwayPath(ctx, ...outer, 10);
      ctx.strokeStyle = `rgba(${accent}, ${(active ? 0.2 : 0.12) * pulse})`;
      ctx.lineWidth = 8;
      ctx.stroke();
    }
    doorwayPath(ctx, ...outer, 10);
    neonStroke(ctx, accent, active || goal ? 2.6 : 1.8, strength * pulse);

    // The exit label rides the portal's projected position, so it follows
    // the door through camera parallax instead of floating in world space.
    if (goal) {
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = `rgba(${accent}, 0.85)`;
      ctx.fillText(side === 'left' ? 'RED EXIT' : 'GREEN EXIT',
        (near.x + far.x) / 2, Math.min(near.t, far.t) - 12);
      ctx.textAlign = 'left';
    }

    // The portal's light landing on the deck below it. A glowing aperture
    // that casts nothing reads as pasted onto the sky; the pool underneath
    // is what places it in the room.
    ctx.save();
    ctx.translate((near.x + far.x) / 2, (near.deck + far.deck) / 2);
    ctx.scale(1, 0.12);
    const cast = ctx.createRadialGradient(0, 0, 0, 0, 0, 90);
    cast.addColorStop(0,
      `rgba(${accent}, ${(active ? 0.26 : goal ? 0.16 : 0.07) * pulse})`);
    cast.addColorStop(1, `rgba(${accent}, 0)`);
    ctx.fillStyle = cast;
    ctx.fillRect(-95, -95, 190, 190);
    ctx.restore();
  };

  /** The cell's structural light: electric blue, like the city's own neon. */
  const CELL_NEON = '90, 170, 255';

  /**
   * Stroke the current path as a neon tube: a wide dim halo, a tighter bloom,
   * the coloured tube, and a pale hot core. Drawn as added light rather than
   * paint, so tubes brighten where they cross and never darken what is behind
   * them — which is what separates an emitter from a painted line.
   *
   * @param {CanvasRenderingContext2D} ctx Path already traced.
   * @param {string} rgb   Tube colour, 'r, g, b'.
   * @param {number} width Tube width in px.
   * @param {number} alpha Overall brightness, 0..1.
   */
  const neonStroke = (ctx, rgb, width, alpha) => {
    if (alpha <= 0.01) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    [[5, 0.1], [2.4, 0.26], [1, 0.9]].forEach(([k, a]) => {
      ctx.strokeStyle = `rgba(${rgb}, ${alpha * a})`;
      ctx.lineWidth = width * k;
      ctx.stroke();
    });
    ctx.strokeStyle = `rgba(235, 247, 255, ${alpha * 0.6})`;
    ctx.lineWidth = Math.max(0.6, width * 0.35);
    ctx.stroke();
    ctx.restore();
  };

  /**
   * The truss's hang depth at a given cell depth — ONE function shared by
   * every truss member, so members that meet at a corner agree on their
   * size there and the ring reads as a single welded frame.
   *
   * @param {number} d Cell depth (see cellGeometry).
   * @returns {number} Vertical hang in screen px.
   */
  const cellTrussH = (d) => Math.max(6, 14 + d * 20);

  /**
   * A truss hanging below an edge: the top chord IS the edge line, with a
   * parallel bottom chord and a web of vertical posts and alternating
   * diagonals between them. The hang is VERTICAL (screen-down) rather than
   * perpendicular to the edge — that is what makes two trusses meeting at a
   * corner share their chord endpoints instead of crossing at an angle,
   * which is where the previous version visibly misaligned.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x1 Near end of the edge line.
   * @param {number} y1
   * @param {number} h1 Hang depth at the near end.
   * @param {number} x2 Far end.
   * @param {number} y2
   * @param {number} h2 Hang depth at the far end.
   * @param {number} alpha
   */
  const drawTruss = (ctx, x1, y1, h1, x2, y2, h2, alpha) => {
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    const top = (t) => [x1 + (x2 - x1) * t, y1 + (y2 - y1) * t];
    const bot = (t) => {
      const [px, py] = top(t);
      return [px, py + h1 + (h2 - h1) * t];
    };

    // Web first, so the chords cap it.
    const panels = Math.max(3, Math.min(20, Math.round(len / 26)));
    ctx.strokeStyle = `rgba(120, 200, 255, ${alpha * 0.55})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= panels; i++) {
      const [tx, ty] = top(i / panels);
      const [bx, by] = bot(i / panels);
      ctx.moveTo(tx, ty);
      ctx.lineTo(bx, by);
      if (i < panels) {
        // Diagonals alternate direction panel to panel (a Warren web).
        const [dx2, dy2] = i % 2 === 0 ? top((i + 1) / panels) : bot((i + 1) / panels);
        ctx.moveTo(...(i % 2 === 0 ? [bx, by] : [tx, ty]));
        ctx.lineTo(dx2, dy2);
      }
    }
    ctx.stroke();

    // Chords: a dark member with an emissive line, the top heavier — it is
    // the cell's own edge.
    [[top, 2.5, 1.2, 1], [bot, 2, 1, 0.8]].forEach(([chord, dark, accent, k]) => {
      const [ax, ay] = chord(0);
      const [bx, by] = chord(1);
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.strokeStyle = `rgba(22, 38, 64, ${alpha * k})`;
      ctx.lineWidth = dark;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      if (chord === top) {
        // The top chord is the cell's own edge, so it carries the light.
        neonStroke(ctx, CELL_NEON, accent, alpha * 0.8);
      } else {
        ctx.strokeStyle = `rgba(150, 225, 255, ${alpha * 0.7 * k})`;
        ctx.lineWidth = accent;
        ctx.stroke();
      }
    });

    // Gusset nodes where the web meets the bottom chord.
    ctx.fillStyle = `rgba(150, 225, 255, ${alpha * 0.55})`;
    for (let i = 0; i <= panels; i++) {
      const [bx, by] = bot(i / panels);
      ctx.fillRect(bx - 1.2, by - 1.2, 2.4, 2.4);
    }
  };

  /**
   * A dark tapering plate along a wall's base line — weight without light.
   * Deliberately no emissive core: glowing baseplates outlined a floor
   * rectangle on the deck, and the cell is not supposed to have a floor.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x1 Near end.
   * @param {number} y1
   * @param {number} w1 Width at the near end.
   * @param {number} x2 Far end.
   * @param {number} y2
   * @param {number} w2 Width at the far end.
   * @param {number} alpha
   */
  const drawBasePlate = (ctx, x1, y1, w1, x2, y2, w2, alpha) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    ctx.beginPath();
    ctx.moveTo(x1 + (nx * w1) / 2, y1 + (ny * w1) / 2);
    ctx.lineTo(x2 + (nx * w2) / 2, y2 + (ny * w2) / 2);
    ctx.lineTo(x2 - (nx * w2) / 2, y2 - (ny * w2) / 2);
    ctx.lineTo(x1 - (nx * w1) / 2, y1 - (ny * w1) / 2);
    ctx.closePath();
    ctx.fillStyle = `rgba(22, 38, 64, ${alpha})`;
    ctx.fill();
  };

  /**
   * A rounded structural frame around one cross-section of the cell — a dark
   * member with an emissive accent inset, corners rounded in proportion to
   * the section's size so the bevel itself sits in perspective.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} f Cross-section from cellGeometry's faceAt.
   * @param {object} weights Line widths and alphas for both layers.
   */
  const drawSectionFrame = (ctx, f, { dark, accent, darkAlpha, accentAlpha }) => {
    const w = f.r - f.l;
    const radius = w * 0.035;
    ctx.beginPath();
    ctx.roundRect(f.l, f.t, w, f.b - f.t, radius);
    ctx.strokeStyle = `rgba(22, 38, 64, ${darkAlpha})`;
    ctx.lineWidth = dark;
    ctx.stroke();

    // The light runs up the sides and across the top, and stops short of the
    // deck: a lit bottom edge on every section outlines a floor rectangle,
    // and the cell has no floor of its own — the roof is its floor.
    ctx.beginPath();
    ctx.moveTo(f.l, f.b - radius);
    ctx.lineTo(f.l, f.t + radius);
    ctx.arcTo(f.l, f.t, f.l + radius, f.t, radius);
    ctx.lineTo(f.r - radius, f.t);
    ctx.arcTo(f.r, f.t, f.r, f.t + radius, radius);
    ctx.lineTo(f.r, f.b - radius);
    neonStroke(ctx, CELL_NEON, accent, Math.min(1, accentAlpha * 1.6));
  };

  /**
   * The surfaces between two cross-sections: ceiling and side-wall glass,
   * tapering corner rails, and the grounding work where the walls meet the
   * deck — baseplates and contact shadows instead of a glass floor. The cell
   * has no floor pane of its own: the deck IS its floor, and drawing glass
   * over the deck was what made the box read as floating in space.
   *
   * Called twice per frame — once for the half behind the fighters and once
   * for the half in front — which is what puts them inside the cell rather
   * than against its face.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} g cellGeometry result.
   * @param {object} a Nearer cross-section.
   * @param {object} b Further cross-section.
   * @param {number} da Depth of a.
   * @param {number} db Depth of b.
   * @param {object} opts Glass opacity for this band.
   */
  const drawCellBand = (ctx, g, a, b, da, db, { alpha }) => {
    const quad = (pts, fill) => {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
    };

    // Ceiling glass — the surface most side-on to the eye, so it carries the
    // depth.
    quad([[a.l, a.t], [b.l, b.t], [b.r, b.t], [a.r, a.t]],
      `rgba(120, 190, 240, ${alpha})`);

    // Side-wall glass. The doorways are not cut out of the pane: at this
    // alpha the pane is barely a tint, and the portal painted over it is
    // what supplies the opening.
    quad([[a.l, a.t], [b.l, b.t], [b.l, b.b], [a.l, a.b]],
      `rgba(150, 220, 255, ${alpha})`);
    quad([[a.r, a.t], [b.r, b.t], [b.r, b.b], [a.r, a.b]],
      `rgba(150, 220, 255, ${alpha})`);

    // Contact shadows: the deck darkening along the wall bases is the
    // strongest "standing on the roof" cue there is.
    const spread = 14;
    const baseShadow = (xNear, xFar, yNear, yFar, dir) => {
      const x0 = (xNear + xFar) / 2;
      const grad = ctx.createLinearGradient(x0, 0, x0 + dir * spread, 0);
      grad.addColorStop(0, 'rgba(0, 0, 0, 0.26)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      quad([[xNear, yNear], [xFar, yFar],
        [xFar + dir * spread, yFar], [xNear + dir * spread, yNear]], grad);
    };
    baseShadow(a.l, b.l, a.b, b.b, 1);
    baseShadow(a.r, b.r, a.b, b.b, -1);

    // Structure along the four corners, tapering with distance, starting
    // where the rounded section corners end (mid-arc, hence the 0.29
    // factor). Top edges carry trusses — the girders the glass hangs from —
    // while the base is a dark plate: weight at the deck, light up high.
    // Truss attach points sit ON the top lines (no y-inset) using the same
    // inset and cellTrussH as the roof girders, so chords meet at corners.
    const inset = (f) => (f.r - f.l) * 0.035 * 0.29;
    const ia = inset(a);
    const ib = inset(b);
    const railAlpha = Math.min(0.9, alpha * 18);
    drawTruss(ctx, a.l + ia, a.t, cellTrussH(da),
      b.l + ib, b.t, cellTrussH(db), railAlpha);
    drawTruss(ctx, a.r - ia, a.t, cellTrussH(da),
      b.r - ib, b.t, cellTrussH(db), railAlpha);
    drawBasePlate(ctx, a.l + ia, a.b, 5.5 + da * 7,
      b.l + ib, b.b, 5.5 + db * 7, railAlpha * 0.8);
    drawBasePlate(ctx, a.r - ia, a.b, 5.5 + da * 7,
      b.r - ib, b.b, 5.5 + db * 7, railAlpha * 0.8);
  };

  /**
   * A mounting foot where a wall member meets the deck — a small plinth
   * that covers the rounded frame corner's gap at the ground and nails the
   * structure to the surface it stands on.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x Base line position.
   * @param {number} y Deck height at that position.
   * @param {number} w Foot width.
   */
  const drawCellFoot = (ctx, x, y, w) => {
    ctx.beginPath();
    ctx.moveTo(x - w / 2, y + 3);
    ctx.lineTo(x - w / 3, y - 5);
    ctx.lineTo(x + w / 3, y - 5);
    ctx.lineTo(x + w / 2, y + 3);
    ctx.closePath();
    ctx.fillStyle = 'rgba(22, 38, 64, 0.9)';
    ctx.fill();
    ctx.fillStyle = `rgba(${CELL_NEON}, 0.75)`;
    ctx.fillRect(x - w / 3, y - 5, (w * 2) / 3, 1.5);
  };

  /**
   * The half of the cell behind the fighters: rear face, the surfaces
   * running back to it, and the doorway portals. Drawn before them, so a
   * fighter crossing an edge is IN the doorway rather than pasted over it.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} screen
   * @param {number} [offsetX] Room origin (see cellGeometry).
   * @param {number} [vpX] Shared vanishing point (see cellGeometry).
   * @param {object} [opts] skipDoor: 'left'|'right' — during the dolly the
   *   trailing room's door at the shared boundary is the live room's door
   *   seen again; drawing both stacks two portals a wall apart.
   */
  const drawCellBehind = (ctx, screen, offsetX = 0,
    vpX = screenWidth(screen) / 2, { skipDoor = null } = {}) => {
    const g = cellGeometry(screen, offsetX, vpX);
    const now = performance.now() / 1000;

    // The cell's glow pooling on the deck inside it — light landing on the
    // floor ties the structure to the surface it stands on.
    ctx.save();
    ctx.translate((g.back.l + g.back.r) / 2, (g.mid.b + g.back.b) / 2);
    ctx.scale(1, 0.08);
    const pool = ctx.createRadialGradient(0, 0, 0, 0, 0, 320);
    pool.addColorStop(0, 'rgba(120, 200, 255, 0.11)');
    pool.addColorStop(1, 'rgba(120, 200, 255, 0)');
    ctx.fillStyle = pool;
    ctx.fillRect(-330, -330, 660, 660);
    ctx.restore();

    // Rear face — the furthest surface, so the faintest glass, but framed
    // like every other section so the structure reads front to back.
    const bw = g.back.r - g.back.l;
    ctx.beginPath();
    ctx.roundRect(g.back.l, g.back.t, bw, g.back.b - g.back.t, bw * 0.035);
    ctx.fillStyle = 'rgba(120, 190, 240, 0.04)';
    ctx.fill();
    drawSectionFrame(ctx, g.back,
      { dark: 3, accent: 1.2, darkAlpha: 0.5, accentAlpha: 0.28 });
    // The rear roof girder, closing the truss ring the depth edges start.
    // Same inset and height as the depth trusses' back ends, so the corner
    // joints land on shared points.
    const backIn = bw * 0.035 * 0.29;
    drawTruss(ctx, g.back.l + backIn, g.back.t, cellTrussH(-0.34),
      g.back.r - backIn, g.back.t, cellTrussH(-0.34), 0.5);

    // Contact shadow at the rear wall's base, spilling toward the camera.
    const rearShadow = ctx.createLinearGradient(0, g.back.b, 0, g.back.b + 10);
    rearShadow.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
    rearShadow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = rearShadow;
    ctx.fillRect(g.back.l, g.back.b, bw, 10);

    drawCellBand(ctx, g, g.mid, g.back, 0, -0.34, { alpha: 0.035 });

    // A panel rib partway back — the division that says "built structure"
    // rather than "one seamless pane".
    drawSectionFrame(ctx, g.faceAt(-0.17),
      { dark: 2, accent: 1, darkAlpha: 0.3, accentAlpha: 0.15 });

    if (skipDoor !== 'left') drawDoorPortal(ctx, g, 'left', now);
    if (skipDoor !== 'right') drawDoorPortal(ctx, g, 'right', now);

    // Feet at the back corners and at the fight plane's wall bases.
    drawCellFoot(ctx, g.back.l, g.back.b, 9);
    drawCellFoot(ctx, g.back.r, g.back.b, 9);
    drawCellFoot(ctx, g.mid.l, g.mid.b, 12);
    drawCellFoot(ctx, g.mid.r, g.mid.b, 12);
  };

  /**
   * Shimmer rings on the glass where a fighter presses against a side wall
   * outside the portal band — the visual for the advancement gate, so a
   * blocked push reads as "hit glass" rather than "the game ignored me".
   * Derived from player position each frame: no state, nothing to sync.
   */
  const drawGlassContact = (ctx, g) => {
    const game = gameStateRef.current;
    const now = performance.now() / 1000;
    Object.values(game.players).forEach((p) => {
      if (!p || p.vaporizing > 0) return;
      const wallX = screenWidth(game.currentScreen) - 14;
      const side = p.x <= 14 ? 'left' : p.x >= wallX ? 'right' : null;
      if (!side || fitsThroughCellDoor(p.y)) return; // lined up: in the portal
      const x = side === 'left' ? g.mid.l : g.mid.r;
      const cy = p.y - 26; // body centre, not the feet
      const pulse = 0.5 + 0.5 * Math.sin(now * 10);
      for (let i = 0; i < 2; i++) {
        const radius = 10 + i * 9 + pulse * 4;
        ctx.beginPath();
        ctx.ellipse(x, cy, radius * 0.45, radius, 0, 0, Math.PI * 2);
        ctx.strokeStyle =
          `rgba(190, 240, 255, ${(0.4 - i * 0.15) * (0.5 + pulse * 0.5)})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    });
  };

  /**
   * The half in front of the fighters. Drawn after them, so they are genuinely
   * behind this glass.
   *
   * Kept far fainter than the back half and given no fill across the opening —
   * a pane the player has to look through is a pane over the only thing they
   * need to read. All of the work is done by the frame and the corner rails,
   * which sit outside the fight rather than on it.
   */
  const drawCellFront = (ctx, screen, offsetX = 0,
    vpX = screenWidth(screen) / 2, { contact = true } = {}) => {
    const g = cellGeometry(screen, offsetX, vpX);

    drawCellBand(ctx, g, g.front, g.mid, 0.2, 0, { alpha: 0.03 });

    // A panel rib partway forward, matching the one behind the fight plane.
    drawSectionFrame(ctx, g.faceAt(0.1),
      { dark: 2, accent: 1, darkAlpha: 0.22, accentAlpha: 0.12 });

    // The mouth of the cell — the heaviest frame member, rounded like the
    // rest of the structure — standing on its own feet, with the front roof
    // girder closing the truss ring. (In portrait this girder is the line
    // crossing the sky — it is structure, not a seam.)
    drawSectionFrame(ctx, g.front,
      { dark: 8, accent: 2.5, darkAlpha: 0.75, accentAlpha: 0.5 });
    const frontIn = (g.front.r - g.front.l) * 0.035 * 0.29;
    drawTruss(ctx, g.front.l + frontIn, g.front.t, cellTrussH(0.2),
      g.front.r - frontIn, g.front.t, cellTrussH(0.2), 0.6);
    drawCellFoot(ctx, g.front.l, g.front.b, 15);
    drawCellFoot(ctx, g.front.r, g.front.b, 15);

    // A single raking highlight across the front pane. One streak is enough to
    // say "there is glass here"; a full sheen greys out the fight behind it.
    const sheen = ctx.createLinearGradient(
      g.front.l, g.front.t, g.front.r * 0.55, g.front.b
    );
    sheen.addColorStop(0, 'rgba(190, 240, 255, 0)');
    sheen.addColorStop(0.45, 'rgba(190, 240, 255, 0.05)');
    sheen.addColorStop(0.55, 'rgba(190, 240, 255, 0.05)');
    sheen.addColorStop(1, 'rgba(190, 240, 255, 0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(g.front.l, g.front.t,
      g.front.r - g.front.l, g.front.b - g.front.t);

    // The shimmer belongs to the room the fighters are actually in — the
    // dolly's trailing room has no one left to press its glass.
    if (contact) drawGlassContact(ctx, g);
  };

  /**
   * Draw the beam windup: an aiming line down the firing lane plus a swelling
   * muzzle orb. This is the whole point of the charge — the opponent has to be
   * able to see the lane before the shot exists.
   */
  const drawBeamCharge = (ctx, player) => {
    if (player.beamCharge <= 0) return;

    const { x, y, direction } = getMuzzle(player);
    const progress = 1 - player.beamCharge / BEAM_CHARGE_TIME; // 0 -> 1

    // Aiming lane, brightening as the shot approaches.
    const startX = direction > 0 ? x : 0;
    const roomW = screenWidth(gameStateRef.current.currentScreen);
    const width = direction > 0 ? roomW - x : x;
    ctx.fillStyle = `rgba(0, 255, 255, ${0.08 + 0.2 * progress})`;
    ctx.fillRect(startX, y - 1, width, 2);

    // Muzzle orb.
    const radius = 3 + 8 * progress;
    const orb = ctx.createRadialGradient(x, y, 0, x, y, radius);
    orb.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
    orb.addColorStop(0.45, 'rgba(0, 255, 255, 0.75)');
    orb.addColorStop(1, 'rgba(0, 255, 255, 0)');
    ctx.fillStyle = orb;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  /**
   * Compact status pip above a fighter's head. Replaces the old floating
   * RIDE/SHOOT text, which overlapped into an unreadable smear whenever the
   * two players closed on each other. Doubles as a team-colour marker.
   */
  const drawPlayerPip = (ctx, player, playerId) => {
    const width = 26;
    const height = 4;
    const left = player.x - width / 2;
    const top = player.y + BODY_TOP - 10;

    // Health sits above the status pip rather than replacing it: the two answer
    // different questions ("how close to dead" vs "is the beam ready"), and a
    // damaged fighter in Hands mode needs both at once.
    if (player.hp < MELEE_MAX_HP) {
      drawHealthBar(ctx, player, playerId, left, top - 7, width);
    }

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(left - 1, top - 1, width + 2, height + 2);

    // A pointer from the pip down to the head it belongs to, so in a tangle
    // of two fighters each readout still says whose it is.
    ctx.fillStyle = teamColor(playerId);
    ctx.beginPath();
    ctx.moveTo(player.x - 3.5, top + height + 2);
    ctx.lineTo(player.x + 3.5, top + height + 2);
    ctx.lineTo(player.x, top + height + 6);
    ctx.closePath();
    ctx.fill();

    if (player.stunned > 0) {
      // Shocked: flashing amber.
      ctx.fillStyle = Math.floor(player.stunned / 100) % 2 ? '#ffd166' : '#7a5a12';
      ctx.fillRect(left, top, width, height);
      return;
    }

    if (player.onBoard && player.boardMode === 'hands') {
      // Beam readiness — the one number that matters in Hands mode.
      const ready = player.beamRecharge <= 0;
      const fill = ready ? 1 : 1 - player.beamRecharge / BEAM_RECHARGE_TIME;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fillRect(left, top, width, height);
      ctx.fillStyle = ready ? '#00ffff' : '#fffa65';
      ctx.fillRect(left, top, width * fill, height);
      return;
    }

    ctx.fillStyle = teamColor(playerId);
    ctx.fillRect(left, top, width, height);
  };

  /**
   * Melee health, drawn above the status pip whenever a fighter is carrying
   * damage.
   *
   * Two things this has to get right, both of which the first version did not:
   *
   * It moves on EVERY hit. Drawn as one tick per punch, the bar quantised two
   * HP into one tick — so a sweep (1 damage) and the odd half of a straddle
   * punch (3) changed nothing on screen, and five of the twelve HP steps
   * rendered identically. A player watching the bar could not tell a landed
   * sweep from a whiffed one. The fill is continuous now and the separators are
   * decoration over the top of it, so a single point of damage always shows.
   *
   * And it is drawn whether or not the board is in hand. Melee damage
   * deliberately survives remounting — backing off to pick your board up is an
   * escape, not a heal — so hiding the bar the moment someone remounts states
   * the opposite of the rule it is there to communicate.
   */
  const drawHealthBar = (ctx, player, playerId, left, top, width) => {
    const height = 3;
    const fraction = Math.max(0, player.hp) / MELEE_MAX_HP;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(left - 1, top - 1, width + 2, height + 2);
    // Depleted ground reads as damage taken rather than as empty space.
    ctx.fillStyle = 'rgba(255, 71, 87, 0.28)';
    ctx.fillRect(left, top, width, height);

    // One punch from death: pulse, because at that point the exact number
    // matters more than the proportion and a thin sliver is easy to miss.
    const lethal = player.hp <= MOVES.punch.damage;
    ctx.fillStyle = lethal
      ? (Math.floor(animPhaseRef.current / 6) % 2 ? '#ffffff' : '#ff4757')
      : teamColor(playerId);
    ctx.fillRect(left, top, width * fraction, height);

    // Punch-interval separators, so the bar can still be counted in hits
    // ("two more punches") as well as read as a proportion.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    for (let hp = MOVES.punch.damage; hp < MELEE_MAX_HP; hp += MOVES.punch.damage) {
      ctx.fillRect(left + Math.round(width * (hp / MELEE_MAX_HP)), top, 1, height);
    }
  };

  /**
   * Devices in flight.
   *
   * A shell tumbles and trails; a submunition is a smaller, brighter mote. Both
   * carry their owner's colour, because the one thing you must be able to read
   * off a thing arcing toward you is whose it is.
   */
  const drawOrdnance = (ctx, game) => {
    game.ordnance.forEach((d) => {
      const rgb = d.owner === 'player1' ? '0, 255, 65' : '255, 71, 87';
      const emp = d.kind === 'emp';
      const r = d.sub ? 3.5 : 7;

      // Trail, so the arc itself is legible rather than just the object on it.
      for (let i = 1; i <= 4; i++) {
        ctx.globalAlpha = 0.2 * (1 - i / 5);
        ctx.fillStyle = `rgb(${rgb})`;
        ctx.beginPath();
        ctx.arc(d.x - d.vx * i * 0.7, d.y - d.vy * i * 0.7, r * 0.7, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      ctx.save();
      ctx.translate(d.x, d.y);
      ctx.rotate(d.spin || 0);
      // Body: an EMP is a pale cell, a cluster shell is a dark casing. Both get
      // a hot rim in the owner's colour.
      ctx.fillStyle = emp ? 'rgba(190, 240, 255, 0.95)' : 'rgba(20, 26, 38, 0.95)';
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = emp ? 'rgba(120, 230, 255, 0.95)' : `rgba(${rgb}, 0.95)`;
      ctx.lineWidth = 2;
      ctx.stroke();
      // A fuse light that quickens as the fuse runs down, so a device about to
      // go off looks like one.
      const urgency = Math.max(0, 1 - d.fuse / 900);
      if (Math.sin(animPhaseRef.current * (0.4 + urgency * 1.6)) > 0) {
        ctx.fillStyle = emp ? 'rgba(220, 250, 255, 1)' : 'rgba(255, 220, 120, 1)';
        ctx.beginPath();
        ctx.arc(0, -r - 1, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });
  };

  /**
   * Blast rings.
   *
   * Drawn as an expanding ring rather than a ball of particles because the
   * thing a player needs off a blast is its REACH — whether they were inside
   * it, and how close the next one can afford to be. The ring ends exactly on
   * the radius the simulation used.
   */
  const drawBlasts = (ctx, game) => {
    game.blasts.forEach((b) => {
      const emp = b.kind === 'emp';
      const t = 1 - b.life / (emp ? EMP_SWEEP_TIME : BLAST_LIFE);
      // The ring ends exactly on the reach the simulation used, so what you see
      // is what caught you. Cluster rings snap out; the EMP's crosses the arena
      // over its whole life, because that travel IS the weapon.
      const radius = emp ? b.max * t : (b.max || CLUSTER_BLAST_RADIUS) * Math.min(1, t * 1.6);
      const fade = 1 - t;

      ctx.strokeStyle = emp
        ? `rgba(150, 235, 255, ${0.9 * fade})`
        : `rgba(255, 190, 90, ${0.9 * fade})`;
      ctx.lineWidth = emp ? 3 : 4;
      ctx.beginPath();
      ctx.arc(b.x, b.y, radius, 0, Math.PI * 2);
      ctx.stroke();

      // A softer inner wash, so the middle of the blast is not a hole.
      const wash = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, radius);
      wash.addColorStop(0, emp
        ? `rgba(120, 220, 255, ${0.3 * fade})`
        : `rgba(255, 160, 60, ${0.34 * fade})`);
      wash.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = wash;
      ctx.beginPath();
      ctx.arc(b.x, b.y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  };

  /** Draw both fighters, their loose boards, windups and status pips. */
  const drawFighters = (ctx, game) => {
    Object.keys(game.players).forEach(playerId => {
      const player = game.players[playerId];

      // A loose board is visible even while its owner is being vaporized.
      if (!player.onBoard) {
        drawLooseBoard(ctx, player.board, playerId, player.boardDown > 0);
      }

      const dying = player.vaporizing > 0;

      // Drive glow goes under the rider, before the body is drawn over it. A
      // dying rider's board has lost power along with them.
      if (player.onBoard && !dying) drawRiderThruster(ctx, player, playerId);

      const playerColor = teamColor(playerId);

      // Prefer the sprite art; fall back to the procedural pixel figure while
      // sprites load (or if they failed to).
      if (!drawPlayerSprite(ctx, player, playerId)) {
        const pose = player.onBoard ? 'riding' : 'standing';
        const pivot = player.onBoard ? 25 : 20;
        ctx.save();
        // The band dissolve needs a sprite to shred. The fallback figure is
        // drawn from rectangles, so it just fades — a plain body standing there
        // for a full second would otherwise read as a rider who did not die.
        if (dying) ctx.globalAlpha *= holoAlpha(1 - player.vaporizing / VAPORIZE_TIME);
        if (player.rotation !== 0) {
          ctx.translate(player.x, player.y - pivot);
          ctx.rotate(player.rotation);
          drawPlayerBody(ctx, 0, 0, playerColor, player.facingRight, pose, player.boardMode);
        } else {
          drawPlayerBody(
            ctx, player.x, player.y - pivot, playerColor, player.facingRight, pose, player.boardMode
          );
        }
        ctx.restore();
      }

      // Neither a windup nor a status pip belongs on a corpse.
      if (dying) return;
      drawBeamCharge(ctx, player);
      drawPlayerPip(ctx, player, playerId);
    });
  };

  /** Draw fired beams: wide soft glow, cyan body, white-hot core. */
  const drawBeams = (ctx, game) => {
    game.beams.forEach(beam => {
      const fade = Math.min(beam.life / 200, 1);
      const startX = Math.min(beam.x, beam.endX);
      const width = Math.abs(beam.endX - beam.x);

      ctx.fillStyle = `rgba(0, 255, 255, ${0.18 * fade})`;
      ctx.fillRect(startX, beam.y - 11, width, 22); // outer glow
      ctx.fillStyle = `rgba(0, 255, 255, ${0.85 * fade})`;
      ctx.fillRect(startX, beam.y - 5, width, 10); // beam body
      ctx.fillStyle = `rgba(255, 255, 255, ${0.9 * fade})`;
      ctx.fillRect(startX, beam.y - 2, width, 4); // white-hot core

      // Splash where the shot was stopped by cover, so blocked shots read as
      // blocked rather than simply short.
      if (beam.endX > 0 && beam.endX < screenWidth(game.currentScreen)) {
        const splash = ctx.createRadialGradient(beam.endX, beam.y, 0, beam.endX, beam.y, 26);
        splash.addColorStop(0, `rgba(255, 255, 255, ${0.9 * fade})`);
        splash.addColorStop(0.4, `rgba(0, 255, 255, ${0.6 * fade})`);
        splash.addColorStop(1, 'rgba(0, 255, 255, 0)');
        ctx.fillStyle = splash;
        ctx.fillRect(beam.endX - 26, beam.y - 26, 52, 52);
      }
    });
  };

  /**
   * Draw the screen's obstacles as lit rooftop structures: dark mass, a neon
   * top edge that catches the city light, and kind-specific detailing.
   */
  const drawObstacles = (ctx, screen) => {
    getObstacles(screen).forEach((o) => {
      // Dark halo first. The backgrounds are busy skylines in the same palette,
      // and without something to separate them, solid geometry the player is
      // meant to read instantly just looks like another distant building.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.fillRect(o.x - 3, o.y - 3, o.w + 6, o.h + 6);

      // Body: dark gunmetal, lit from above. It no longer needs to be lighter
      // than the city to be read — the neon edge below does that job, the way
      // every solid thing in the city is read by its lights.
      const body = ctx.createLinearGradient(0, o.top, 0, o.bottom);
      body.addColorStop(0, '#3a4156');
      body.addColorStop(0.3, '#1f2433');
      body.addColorStop(1, '#10131d');
      ctx.fillStyle = body;
      ctx.fillRect(o.x, o.y, o.w, o.h);
      // Side bevels, so the block has thickness rather than being a card.
      ctx.fillStyle = 'rgba(150, 180, 230, 0.16)';
      ctx.fillRect(o.x, o.y, 1, o.h);
      ctx.fillRect(o.right - 1, o.y, 1, o.h);

      // Contact shadow so it sits on the surface below it.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(o.x + 3, o.bottom, o.w - 6, 4);

      // Standable top: a neon strip, the one line the player has to read
      // instantly — this is where you can land.
      ctx.beginPath();
      ctx.moveTo(o.x + 1, o.y + 1);
      ctx.lineTo(o.right - 1, o.y + 1);
      neonStroke(ctx, CELL_NEON, 1.8, 0.85);

      if (o.kind === 'billboard') {
        // Vertical neon sign: a lit screen in a dark frame, lettered top to
        // bottom like the city's own signs.
        const face = ctx.createLinearGradient(0, o.y + 6, 0, o.bottom - 6);
        face.addColorStop(0, 'rgba(255, 70, 160, 0.55)');
        face.addColorStop(1, 'rgba(120, 60, 255, 0.5)');
        ctx.fillStyle = face;
        ctx.fillRect(o.x + 4, o.y + 6, o.w - 8, o.h - 12);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
        for (let y = o.y + 8; y < o.bottom - 8; y += 3) {
          ctx.fillRect(o.x + 4, y, o.w - 8, 1);
        }
        ctx.beginPath();
        ctx.rect(o.x + 4, o.y + 6, o.w - 8, o.h - 12);
        neonStroke(ctx, '255, 90, 180', 1.4, 0.8);
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.font = 'bold 11px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(255, 225, 245, 0.95)';
        const word = 'SKYDROP';
        const pitch = (o.h - 24) / word.length;
        for (let i = 0; i < word.length; i++) {
          ctx.fillText(word[i], o.x + o.w / 2, o.y + 20 + i * pitch);
        }
        ctx.restore();
      } else if (o.kind === 'pipe') {
        // Duct: a round highlight along its length, and banding clamps.
        const round = ctx.createLinearGradient(0, o.y, 0, o.bottom);
        round.addColorStop(0, 'rgba(160, 190, 240, 0)');
        round.addColorStop(0.28, 'rgba(160, 190, 240, 0.14)');
        round.addColorStop(0.5, 'rgba(160, 190, 240, 0)');
        ctx.fillStyle = round;
        ctx.fillRect(o.x, o.y, o.w, o.h);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        for (let x = o.x + 10; x < o.right - 6; x += 18) {
          ctx.fillRect(x, o.y + 2, 3, o.h - 4);
        }
        ctx.fillStyle = 'rgba(80, 255, 200, 0.9)';
        ctx.fillRect(o.right - 10, o.y + 8, 3, 3);
      } else if (o.kind === 'vent') {
        // Floor-standing air handler: louvres, and hazard chevrons at the base.
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        for (let y = o.y + 12; y < o.bottom - 10; y += 9) {
          ctx.fillRect(o.x + 5, y, o.w - 10, 3);
        }
        ctx.save();
        ctx.beginPath();
        ctx.rect(o.x, o.bottom - 7, o.w, 5);
        ctx.clip();
        ctx.fillStyle = 'rgba(255, 190, 60, 0.85)';
        ctx.fillRect(o.x, o.bottom - 7, o.w, 5);
        ctx.fillStyle = 'rgba(12, 12, 16, 0.9)';
        for (let x = o.x - 6; x < o.right; x += 10) {
          ctx.beginPath();
          ctx.moveTo(x, o.bottom - 2);
          ctx.lineTo(x + 5, o.bottom - 7);
          ctx.lineTo(x + 9, o.bottom - 7);
          ctx.lineTo(x + 4, o.bottom - 2);
          ctx.fill();
        }
        ctx.restore();
      } else {
        // Hovering slab: panel seams, a row of amber running lights along
        // its face, and their light spilling onto whatever is beneath it.
        ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
        for (let x = o.x + 36; x < o.right - 12; x += 36) {
          ctx.fillRect(x, o.y + 4, 1, o.h - 6);
        }
        const lampY = o.bottom - 8;
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        for (let x = o.x + 12; x < o.right - 10; x += 18) {
          const lamp = ctx.createRadialGradient(x + 3, lampY + 1.5, 0, x + 3, lampY + 1.5, 8);
          lamp.addColorStop(0, 'rgba(255, 170, 70, 0.35)');
          lamp.addColorStop(1, 'rgba(255, 170, 70, 0)');
          ctx.fillStyle = lamp;
          ctx.fillRect(x - 5, lampY - 6.5, 16, 16);
          ctx.fillStyle = 'rgba(255, 200, 120, 0.95)';
          ctx.fillRect(x, lampY, 6, 3);
        }
        const spill = ctx.createLinearGradient(0, o.bottom, 0, o.bottom + 16);
        spill.addColorStop(0, 'rgba(255, 160, 70, 0.16)');
        spill.addColorStop(1, 'rgba(255, 160, 70, 0)');
        ctx.fillStyle = spill;
        ctx.fillRect(o.x + 4, o.bottom, o.w - 8, 16);
        ctx.restore();
      }
    });
  };

  const drawParticles = (ctx, game) => {
    game.particles.forEach(particle => {
      const alpha = particle.life / 1000;
      // Most particles take their colour from their type; a few (portal sparks)
      // carry an explicit one because it depends on which player caused them.
      const rgb = particle.rgb || PARTICLE_COLORS[particle.type] || '255, 255, 255';
      ctx.fillStyle = `rgba(${rgb}, ${alpha})`;
      ctx.fillRect(particle.x - 2, particle.y - 2, 4, 4);
    });
  };

  // --- HUD ------------------------------------------------------------------

  /**
   * Tug-of-war frontline bar.
   *
   * The match is a push in one dimension, and the old HUD expressed that as the
   * text "Screen: 4/7" in a corner — accurate but with none of the tension.
   * The track runs from Red's exit on the left to Green's exit on the right and
   * the marker is the current front line: drive it to your colour's end to win.
   */
  /**
   * The ordnance readout: what each fighter still has, and what is coming back.
   *
   * Ammunition that recharges is only a decision if you can see it. Three bars
   * for the shells and one ring for the pulse, on each fighter's own side of
   * the tug bar so the two loadouts read the way the arena does — red's on the
   * left, green's on the right, both filling toward the middle.
   *
   * A spent charge is drawn as an empty outline that FILLS as its clock runs,
   * which answers "how long" without a number on screen; and because the
   * cluster clock does not start until the magazine is empty, three outlines
   * with nothing filling is itself the message that the reload has not begun.
   */
  /**
   * The ability readout, on each fighter's own side of the tug bar.
   *
   * The phone has had this since the buttons grew rings; the desktop had only
   * ammunition pips, so a player there could see that they had two shells but
   * not that their beam was four seconds from coming back, or that the board
   * button was dead rather than broken. Those are the readings you plan around.
   *
   * Same four abilities, same colours and the same red-for-unavailable as the
   * buttons use, because they are the same information — see abilityStatus,
   * which both read from. Drawn for BOTH fighters: whether their pulse is up is
   * every bit as worth knowing as whether yours is.
   */
  const ABILITY_BARS = [
    { key: 'board', rgb: '0, 255, 65' },
    { key: 'beam', rgb: '34, 230, 255' },
    { key: 'cluster', rgb: '255, 157, 61' },
    { key: 'emp', rgb: '255, 210, 61' },
  ];

  const drawOrdnanceHud = (ctx, game, view) => {
    const left = (view.width - TUG_BAR_WIDTH) / 2;
    const y = TUG_BAR_Y - 14;
    const w = 22;
    const h = 7;
    const gap = 5;

    ['player2', 'player1'].forEach((slot) => {
      const p = game.players[slot];
      if (!p) return;
      const status = abilityStatus(p);
      const green = slot === 'player1';
      // Each readout starts at its owner's END of the tug bar and runs inward,
      // so the two are mirror images and each sits over the ground its owner is
      // fighting for.
      const edge = green ? left + TUG_BAR_WIDTH : left;

      ABILITY_BARS.forEach((bar, i) => {
        const x = green
          ? edge - (i + 1) * (w + gap) + gap
          : edge + i * (w + gap);
        const { ready, usable } = status[bar.key];
        const rgb = usable ? bar.rgb : '255, 71, 87';

        // Track, then the fill. A bar that is simply absent when empty reads as
        // a missing feature rather than as a weapon coming back.
        ctx.fillStyle = 'rgba(255, 255, 255, 0.09)';
        ctx.fillRect(x, y, w, h);
        ctx.fillStyle = `rgba(${rgb}, ${usable ? 0.92 : 0.6})`;
        ctx.fillRect(x, y, w * ready, h);

        // The cluster is three discrete shells, so its bar is divided into three
        // — the difference between "two thirds charged" and "two of three" is
        // the whole question when you are deciding whether to throw.
        if (bar.key === 'cluster') {
          ctx.fillStyle = 'rgba(8, 12, 20, 0.9)';
          for (let d = 1; d < CLUSTER_MAX; d += 1) {
            ctx.fillRect(x + (w * d) / CLUSTER_MAX - 0.5, y, 1, h);
          }
        }
      });
    });
  };

  const drawTugOfWar = (ctx, game, view) => {
    const left = (view.width - TUG_BAR_WIDTH) / 2;
    const right = left + TUG_BAR_WIDTH;

    // The console tab hanging from the top edge: one dark panel behind the
    // ability readouts, the bar and the status line, stepping in beneath the
    // bar so it frames the status text. The city behind is bright and busy
    // now, and HUD text laid straight onto it competes with the billboards.
    const shoulder = TUG_BAR_Y + TUG_BAR_HEIGHT + 8;
    const base = TUG_BAR_Y + 38;
    ctx.beginPath();
    ctx.moveTo(left - 78, 0);
    ctx.lineTo(right + 78, 0);
    ctx.lineTo(right + 64, shoulder);
    ctx.lineTo(right - 36, base);
    ctx.lineTo(left + 36, base);
    ctx.lineTo(left - 64, shoulder);
    ctx.closePath();
    ctx.fillStyle = 'rgba(6, 9, 20, 0.68)';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(left - 78, 0);
    ctx.lineTo(left - 64, shoulder);
    ctx.lineTo(left + 36, base);
    ctx.lineTo(right - 36, base);
    ctx.lineTo(right + 64, shoulder);
    ctx.lineTo(right + 78, 0);
    ctx.strokeStyle = `rgba(${CELL_NEON}, 0.35)`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Track backing.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(left - 3, TUG_BAR_Y - 3, TUG_BAR_WIDTH + 6, TUG_BAR_HEIGHT + 6);

    // Red's ground on the left, Green's on the right.
    const track = ctx.createLinearGradient(left, 0, left + TUG_BAR_WIDTH, 0);
    track.addColorStop(0, 'rgba(255, 71, 87, 0.85)');
    track.addColorStop(0.5, 'rgba(70, 80, 110, 0.5)');
    track.addColorStop(1, 'rgba(0, 255, 65, 0.85)');
    ctx.fillStyle = track;
    ctx.fillRect(left, TUG_BAR_Y, TUG_BAR_WIDTH, TUG_BAR_HEIGHT);

    // Screen ticks.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    for (let i = 1; i < SCREEN_COUNT; i++) {
      ctx.fillRect(left + (TUG_BAR_WIDTH * i) / (SCREEN_COUNT - 1) - 1, TUG_BAR_Y, 1, TUG_BAR_HEIGHT);
    }

    // Front line marker.
    const markerX = left + (TUG_BAR_WIDTH * game.currentScreen) / (SCREEN_COUNT - 1);
    ctx.fillStyle = '#0b0d16';
    ctx.fillRect(markerX - 4, TUG_BAR_Y - 5, 8, TUG_BAR_HEIGHT + 10);
    ctx.fillStyle = game.advancingPlayer ? teamColor(game.advancingPlayer) : '#ffffff';
    ctx.fillRect(markerX - 2, TUG_BAR_Y - 4, 4, TUG_BAR_HEIGHT + 8);

    // End caps mark whose exit is whose.
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = RED;
    ctx.textAlign = 'right';
    ctx.fillText('RED ◀', left - 8, TUG_BAR_Y + TUG_BAR_HEIGHT);
    ctx.fillStyle = GREEN;
    ctx.textAlign = 'left';
    ctx.fillText('▶ GREEN', left + TUG_BAR_WIDTH + 8, TUG_BAR_Y + TUG_BAR_HEIGHT);
    ctx.textAlign = 'left';
  };

  /** Match status line under the tug-of-war bar. */
  const drawStatusLine = (ctx, game, view) => {
    ctx.textAlign = 'center';
    ctx.font = 'bold 12px monospace';

    if (!game.advancementUnlocked) {
      ctx.fillStyle = '#fffa65';
      ctx.fillText('FIRST KILL UNLOCKS THE PUSH', view.width / 2, TUG_BAR_Y + 30);
    } else if (game.advancingPlayer) {
      // Colour follows whoever actually holds the advance. This used to be
      // hardcoded green, so "RED can advance" rendered in the wrong team colour.
      const advancing = game.advancingPlayer === 'player1' ? 'GREEN' : 'RED';
      ctx.fillStyle = teamColor(game.advancingPlayer);
      ctx.fillText(`${advancing} ADVANCING — REACH THE EDGE`, view.width / 2, TUG_BAR_Y + 30);
    }
    ctx.textAlign = 'left';
  };

  /** Which colour this client is driving (online only). */
  const drawOnlineSlotBadge = (ctx, game, view) => {
    if (game.gameMode !== 'online' || !onlineRef.current) return;
    const isGreen = onlineRef.current.playerSlot === 'player1';
    ctx.font = 'bold 12px monospace';
    ctx.fillStyle = isGreen ? GREEN : RED;
    ctx.fillText(`YOU ARE ${isGreen ? 'GREEN' : 'RED'}`, 12, view.height - 14);
  };

  // --- Full-screen effects --------------------------------------------------

  /** Darken the frame edges so the action reads toward the centre. */
  const drawVignette = (ctx, view) => {
    // Cached, but the cache is keyed on the frame — a rotate or a window resize
    // changes the frame, and a gradient built for the old one would darken the
    // wrong part of the screen.
    if (!vignetteGradient || vignetteKey !== `${view.width}x${view.height}`) {
      vignetteKey = `${view.width}x${view.height}`;
      vignetteGradient = ctx.createRadialGradient(
        view.width / 2, view.height / 2, view.height * 0.5,
        view.width / 2, view.height / 2, view.height * 0.95
      );
      vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
    }
    ctx.fillStyle = vignetteGradient;
    // The FRAME, not the play area. This is drawn after the camera transform is
    // unwound, and the gradient above is built in frame coordinates — filling
    // an 800x600 rectangle left the right-hand side of every wide frame with no
    // vignette at all.
    ctx.fillRect(0, 0, view.width, view.height);
  };

  /** Full-canvas colour wash from the most recent impact. */
  const drawFlash = (ctx, impact, view) => {
    if (impact.flash <= 0.01) return;
    ctx.fillStyle = `rgba(${impact.flashColor}, ${impact.flash})`;
    ctx.fillRect(0, 0, view.width, view.height);
  };

  /**
   * Fireworks over the city for the victory scene. Bursts launch on a random
   * cadence in the sky above the cell's roof, in the winner's colour cut with
   * gold and white. Drawn before the cell, so the shells pop beyond the glass.
   */
  const drawFireworks = (ctx, winner) => {
    const v = victoryRef.current;
    const now = performance.now();
    if (now > v.nextBurst) {
      v.nextBurst = now + 450 + Math.random() * 600;
      const palette = [
        winner === 'player1' ? '0, 255, 65' : '255, 71, 87',
        '255, 215, 0',
        '255, 255, 255',
      ];
      const rgb = palette[Math.floor(Math.random() * palette.length)];
      const roomW = screenWidth(gameStateRef.current.currentScreen);
      const cx = 80 + Math.random() * (roomW - 160);
      // The sky the pan has opened up above the roofline.
      const roof = cellRoofY(sceneEyeRef.current);
      const cy = roof - 360 + Math.random() * 300;
      const count = 24 + Math.floor(Math.random() * 12);
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
        const speed = 1 + Math.random() * 2.4;
        v.sparks.push({
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: 0.008 + Math.random() * 0.008,
          rgb,
        });
      }
      // Backstop against throttled tabs piling bursts up.
      if (v.sparks.length > 900) v.sparks.splice(0, v.sparks.length - 900);
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = v.sparks.length - 1; i >= 0; i--) {
      const s = v.sparks[i];
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.028; // shells arc over and rain
      s.vx *= 0.985;
      s.life -= s.decay;
      if (s.life <= 0) {
        v.sparks.splice(i, 1);
        continue;
      }
      // Twinkle as they die.
      const a = s.life * (s.life > 0.4 ? 1 : 0.4 + 0.6 * Math.random());
      ctx.fillStyle = `rgba(${s.rgb}, ${a})`;
      ctx.fillRect(s.x - 1.2, s.y - 1.2, 2.4, 2.4);
    }
    ctx.restore();
  };

  /**
   * Searchlights in the winner's colour, raking up from behind the roof the
   * champion stands on — the victory mock-up's beams. Drawn before the cell
   * so the structure and the champion stand in front of their bases; additive,
   * so they only ever add light to the night behind. Faded in with the crane.
   *
   * @param {CanvasRenderingContext2D} ctx World space.
   * @param {object} game
   */
  const drawVictoryLights = (ctx, game) => {
    const rgb = game.winner === 'player1' ? '0, 255, 90' : '255, 60, 80';
    const now = performance.now() / 1000;
    const centre = screenWidth(game.currentScreen) / 2;
    const roof = cellRoofY(sceneEyeRef.current);
    const fade = victoryEyeRef.current;
    if (fade < 0.02) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    [-1, -0.4, 0.4, 1].forEach((k, i) => {
      // Each beam leans out from the middle and sways on its own clock.
      const angle = k * 0.34 + Math.sin(now * 0.55 + i * 1.9) * 0.1;
      const length = 640;
      const spread = 64;
      ctx.save();
      ctx.translate(centre + k * 46, roof + 6);
      ctx.rotate(angle);
      const beam = ctx.createLinearGradient(0, 0, 0, -length);
      beam.addColorStop(0, `rgba(${rgb}, ${0.3 * fade})`);
      beam.addColorStop(0.55, `rgba(${rgb}, ${0.1 * fade})`);
      beam.addColorStop(1, `rgba(${rgb}, 0)`);
      ctx.fillStyle = beam;
      ctx.beginPath();
      ctx.moveTo(-3, 0);
      ctx.lineTo(-spread, -length);
      ctx.lineTo(spread, -length);
      ctx.lineTo(3, 0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });
    // Where they start: a glow on the roof behind the champion's feet.
    const pool = ctx.createRadialGradient(centre, roof, 0, centre, roof, 120);
    pool.addColorStop(0, `rgba(${rgb}, ${0.32 * fade})`);
    pool.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = pool;
    ctx.fillRect(centre - 120, roof - 120, 240, 240);
    ctx.restore();
  };

  /**
   * The champion on the roof of the cell, fists up. The jump pose IS the
   * fist-raise — both arms punched skyward — so the winner plays a victory
   * hop: airborne frames use it, landings drop back to idle. Built as a
   * display-only copy of the real player so drawPlayerSprite (rim light,
   * team colours, mirroring) does all the work; every field that could pull
   * in a combat pose is zeroed.
   */
  const drawVictoryWinner = (ctx, game) => {
    const now = performance.now() / 1000;
    const hop = Math.abs(Math.sin(now * 2.8));
    const air = hop > 0.3;
    // Height off the roof: zero for the whole of the landed phase, rising
    // from zero as the airborne one starts. Lifting by the raw cycle left the
    // "landed" frames up to 7px in the air — a speck at fight zoom, but the
    // victory shot cranes in far enough to show the champion floating.
    const lift = air ? ((hop - 0.3) / 0.7) * 24 : 0;
    // Feet on the FRONT roof girder — the cell's nearest and heaviest drawn
    // line, so the champion stands at the front edge of the roof with it
    // receding behind him. Computed from the live eye, which the victory
    // camera is still raising, so his feet track the girder as it moves.
    const roofY = cellRoofY(sceneEyeRef.current);
    drawPlayerSprite(ctx, {
      ...game.players[game.winner],
      x: screenWidth(game.currentScreen) / 2,
      y: roofY - lift,
      vx: 0,
      vy: air ? -3 : 0, // past AIRBORNE_VY -> the fists-up jump pose
      grounded: !air,
      facingRight: game.winner === 'player1',
      onBoard: false,
      boardMode: 'feet',
      rotation: 0,
      transition: null,
      transitionTime: 0,
      vaporizing: 0,
      invuln: 0,
      backflipping: 0,
      attack: null,
      attackTime: 0,
      guarding: false,
      downTime: 0,
      hurtTime: 0,
    }, game.winner);
  };

  /**
   * End-of-match light (win or opponent disconnect): dims and vignettes the
   * frame under the end screen (EndScreen.js), which carries all the words.
   *
   * A win keeps the dim light: the victory scene — the champion on the roof,
   * the spotlights, the fireworks — is the point. A disconnect keeps the old
   * blackout; there is nothing to watch behind that one.
   */
  const drawEndOverlay = (ctx, game, view) => {
    if (!game.winner && !game.disconnected) return;
    ctx.fillStyle = game.winner
      ? 'rgba(4, 6, 14, 0.22)'
      : 'rgba(4, 6, 14, 0.72)';
    ctx.fillRect(0, 0, view.width, view.height);
    drawVignette(ctx, view);
  };

  /**
   * Render one frame.
   *
   * Order matters: the world is drawn inside a shaken transform, then the
   * flash, vignette and HUD are drawn outside it so the interface stays
   * rock-steady while the arena takes the hit.
   */
  const renderGame = (ctx) => {
    if (!ctx) return;

    // Keep pixel art crisp if a transform (backflip rotation) resamples it.
    ctx.imageSmoothingEnabled = false;

    const game = gameStateRef.current;
    const impact = impactRef.current;
    const view = viewRef.current;
    animPhaseRef.current += 1;

    ctx.save();
    if (impact.shake > 0.2) {
      ctx.translate(
        (Math.random() - 0.5) * impact.shake * 2,
        (Math.random() - 0.5) * impact.shake * 2
      );
    }

    // Backdrop fills the frame; everything after it is drawn in the play area's
    // own coordinates, mapped through the camera. That single transform is what
    // lets the entire simulation and every draw call below stay in an 800x600
    // world that has no idea the frame around it moves, zooms or changes shape.
    const cam = cameraRef.current;

    // Screen-change dolly. The live camera has already snapped to the new
    // fight; the EFFECTIVE camera departs from where the old room's framing
    // was (re-based one arena over, since the old room is drawn there) and
    // eases onto the live one — a tracking shot through the shared doorway.
    const t = transitionRef.current;
    const inPan = t.time > 0;
    const ease = inPan ? easeInOut(1 - t.time / SCREEN_TRANSITION_TIME) : 1;
    // How far over the trailing room sits. Not one arena width any more — the
    // two rooms are different sizes, and they have to meet exactly at the
    // doorway they share or the corridor visibly steps. Pushing right, the old
    // room hangs off the left by ITS width; pushing left, it starts at the live
    // room's right wall, so the offset is the LIVE room's width.
    const roomShift = t.direction > 0
      ? screenWidth(t.fromScreen)
      : -screenWidth(game.currentScreen);
    const departX = t.startX - roomShift;
    const eff = inPan
      ? {
        x: departX + (cam.x - departX) * ease,
        y: t.startY + (cam.y - t.startY) * ease,
        zoom: t.startZoom + (cam.zoom - t.startZoom) * ease,
      }
      : cam;

    // The cell's vanishing point follows the camera's centre at all times: a
    // vanishing point belongs to the viewer, not the room, so the box shifts
    // perspective as the camera tracks the fight (real parallax) and stays
    // accurate through the dolly for free. Chosen over a room-centred VP in
    // an A/B on 2026-08-04 — "more of a realistic feel".
    const vpX = eff.x;
    // Resolve the scene's eye for this frame, before anything projects
    // through it: the fight's horizon, crossed toward the camera's own
    // height for the victory shot.
    sceneEyeRef.current =
      HORIZON_Y + (eff.y - HORIZON_Y) * victoryEyeRef.current;

    // The city glides between the rooms' parallax positions in step.
    const screenF = inPan
      ? game.currentScreen - t.direction * (1 - ease)
      : game.currentScreen;

    drawBackdrop(ctx, screenF, view, eff);
    ctx.translate(view.width / 2, view.height / 2);
    ctx.scale(eff.zoom, eff.zoom);
    ctx.translate(-eff.x, -eff.y);

    // The trailing room, one arena over, sharing the live vanishing point —
    // no fighters (they have advanced), no shimmer, and no door on the shared
    // boundary, which belongs to the live room.
    if (inPan) {
      drawArena(ctx, t.fromScreen, -roomShift, vpX,
        { skipDoor: t.direction > 0 ? 'right' : 'left' });
      ctx.save();
      ctx.translate(-roomShift, 0);
      drawObstacles(ctx, t.fromScreen);
      ctx.restore();
    }

    // The victory scene replaces the fight: fireworks beyond the glass, the
    // fighters cleared from the floor, and the champion on the roof (below,
    // after the cell's front half — they stand on top of it, not inside).
    const celebrating = !!game.winner && !game.disconnected;
    if (celebrating) {
      drawFireworks(ctx, game.winner);
      drawVictoryLights(ctx, game);
    }
    else if (victoryRef.current.sparks.length) {
      // A rematch started — the last match's shells don't carry over.
      victoryRef.current.sparks.length = 0;
    }

    drawArena(ctx, game.currentScreen, 0, vpX);
    drawObstacles(ctx, game.currentScreen);
    if (!celebrating) {
      drawFighters(ctx, game);
      drawBeams(ctx, game);
      drawOrdnance(ctx, game);
      drawBlasts(ctx, game);
    }
    drawParticles(ctx, game);
    // The wet deck reflects everything drawn so far. Taken from the live
    // transform, so the mirror line is the floor wherever the camera — or a
    // screen shake — has put it this frame.
    const toFrame = ctx.getTransform();
    drawDeckReflection(ctx, toFrame.d * GROUND_Y + toFrame.f,
      view.width, view.height);
    // The near half of the cell, over the fighters — this is what actually puts
    // them inside the box rather than in front of it. The trailing room's
    // front half first, so the live room's frame reads nearest.
    if (inPan) {
      drawCellFront(ctx, t.fromScreen, -roomShift, vpX, { contact: false });
    }
    drawCellFront(ctx, game.currentScreen, 0, vpX);

    if (celebrating) drawVictoryWinner(ctx, game);

    ctx.restore();

    // Frame-space furniture: these belong to the screen, not the arena.
    drawFlash(ctx, impact, view);
    drawVignette(ctx, view);

    // The HUD stands down once the match is decided; the overlay speaks for it.
    // Upright on a phone the header is page markup instead (MobileHud).
    if (!game.winner && !game.disconnected && !mobileHudRef.current) {
      drawTugOfWar(ctx, game, view);
      drawOrdnanceHud(ctx, game, view);
      drawStatusLine(ctx, game, view);
      drawOnlineSlotBadge(ctx, game, view);
    }

    drawEndOverlay(ctx, game, view);
  };

  mobileHudRef.current = touchPrimary && portrait;
  // The match is decided (or abandoned): the end screen takes over from the
  // in-play chrome — thumb controls, key strip, HUD buttons.
  const matchOver = !!(endState.winner || endState.disconnected);

  // Online lobby takes over the pre-game screen while matchmaking.
  if (!gameStarted && inLobby) {
    return (
      <Lobby
        onCancel={() => setInLobby(false)}
        onGameStart={startOnlineMatch}
        initialRoom={invitedRoom}
      />
    );
  }

  if (!gameStarted) {
    const MODES = [
      // Local needs a second set of controls, and a phone has exactly one set
      // of thumbs — player 2 would be a statue. Say so rather than let someone
      // start a match they cannot play. Offered only where it is switched on
      // (features.js): it waits for the console ports.
      LOCAL_MULTIPLAYER && {
        id: 'local',
        label: 'Local Multiplayer',
        // The keyboard scheme is built for one person with a mouse, so the
        // second player wants their own device — say so on the button rather
        // than let someone find out by starting a match they cannot share.
        blurb: touchPrimary
          ? 'Two fighters — needs a keyboard and a second device'
          : 'Two fighters — plug in a controller for player 2',
        needsKeyboard: true,
      },
      { id: 'ai', label: 'vs AI', blurb: 'Duel the machine' },
      { id: 'online', label: 'Online Multiplayer', blurb: 'Fight across the net' },
    ].filter(Boolean);
    // Records are kept per difficulty tier, so the title has to name the tier —
    // otherwise switching difficulty silently swaps the number underneath a
    // heading that still just says "Record vs AI".
    const difficultyLabel =
      (DIFFICULTIES.find((d) => d.id === difficulty) || DIFFICULTIES[1]).label;
    const recordTitle = gameMode === 'ai'
      ? `Record vs ${difficultyLabel}`
      : gameMode === 'online' ? 'Online Record' : 'Series Record';
    // Confirms what pressing start will actually do, and gives the status
    // panel something between the tally and the button.
    const setupLine = gameMode === 'ai'
      ? `${difficultyLabel} opponent`
      : gameMode === 'online' ? 'Create or join a room' : 'Keyboard + controller';
    // What will ACTUALLY be staked. Clamped once, here, and used by both the
    // slider and the start button — computing it in the slider alone let the
    // menu advertise an affordable stake while start tried to place the raw
    // one, which beginSeries then refused, quietly starting an unstaked match.
    const effectiveStake = Math.max(
      MIN_STAKE, Math.min(stake, balance, MAX_STAKE)
    );

    return (
      <div className="landing">
        {/* Key art first, then the two scrims that make HUD text survive on top
            of it: a vignette to darken the edges and a scanline wash. Without
            them the neon city behind the console competes with the console. */}
        <div
          className="landing-art"
          style={{ backgroundImage: `url(${menuHero})` }}
        />
        <div className="landing-scrim" />
        <div className="landing-scan" />

        <div className="hud">
          <header className="hud-strip">
            <span className="hud-chip hud-chip-live">COMBAT NET ONLINE</span>
            <span className="hud-chip hud-dim">SECTOR 07 — NIGHT CYCLE</span>
            <span className="hud-chip hud-dim">
              {gameMode === 'online' ? 'UPLINK READY' : 'LOCAL LINK'}
            </span>
            <button
              className={`hud-chip hud-chip-btn${muted ? ' muted' : ''}`}
              onClick={() => setMutedState((m) => !m)}
              aria-pressed={muted}
            >
              {muted ? '\u{1F507} SOUND OFF' : '\u{1F50A} SOUND ON'}
            </button>
          </header>

          <div className="hud-title">
            {/* The wordmark is art; the heading's text is kept for screen
                readers (and anything else reading the page as text). */}
            <h1 className="hud-logo">
              <img src={logoArt} alt="" />
              <span className="sr-only">Sky-Drop</span>
            </h1>
            <p className="hud-tagline">Drive them back through the portal.</p>
          </div>

          <main className="hud-console">
            <section className="hud-panel hud-panel-modes">
              <span className="panel-label">Select Mode</span>
              <div className="mode-list">
                {MODES.map((mode, i) => (
                  <button
                    key={mode.id}
                    className={`mode-btn ${gameMode === mode.id ? 'active' : ''}`}
                    aria-pressed={gameMode === mode.id}
                    onClick={() => setGameMode(mode.id)}
                    style={{ '--card-art': `url(${MODE_ART[mode.id]})` }}
                  >
                    <span className="mode-index">{`0${i + 1}`}</span>
                    <span className="mode-text">
                      <span className="mode-name">{mode.label}</span>
                      <span className="mode-blurb">{mode.blurb}</span>
                    </span>
                  </button>
                ))}
              </div>

              {gameMode === 'ai' && (
                <div className="difficulty-row">
                  <span className="panel-label panel-label-inline">Difficulty</span>
                  {DIFFICULTIES.map((level) => (
                    <button
                      key={level.id}
                      // Keyed on the id, not the visible text: "X-Hard"
                      // contains "Hard", so anything selecting a button by its
                      // label matches two of them.
                      data-level={level.id}
                      className={`difficulty-btn ${difficulty === level.id ? 'active' : ''}`}
                      aria-pressed={difficulty === level.id}
                      onClick={() => setDifficulty(level.id)}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Ranked: put play money on a best-of-three against the AI.
                  Only offered vs the AI — a stake needs a result the game
                  decides, and against another person that result currently
                  comes from whoever is hosting. Opt-in, so nobody stakes by
                  pressing Start out of habit. */}
              {gameMode === 'ai' && (
                <div className="ranked-block">
                  <label className="ranked-toggle">
                    <input
                      type="checkbox"
                      checked={ranked}
                      onChange={(e) => setRanked(e.target.checked)}
                    />
                    <span>Ranked — stake play money</span>
                  </label>

                  <div className="wallet-line">
                    <span className="wallet-label">Balance</span>
                    <span className="wallet-amount">{balance}</span>
                    {balance <= TOP_UP_CEILING && (
                      <button
                        className="wallet-topup"
                        onClick={() => setBalance(topUp())}
                      >
                        Top up
                      </button>
                    )}
                  </div>

                  {ranked && (
                    <div className="stake-row">
                      <input
                        className="stake-slider"
                        type="range"
                        min={MIN_STAKE}
                        max={Math.min(MAX_STAKE, balance)}
                        step={10}
                        value={effectiveStake}
                        onChange={(e) => setStake(Number(e.target.value))}
                        aria-label="Stake"
                      />
                      <span className="stake-terms">
                        Stake <b>{effectiveStake}</b>
                        {' · win '}
                        <b className="stake-win">
                          +{winningsFor(effectiveStake, difficulty)}
                        </b>
                        {' · best of three'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="hud-panel hud-panel-status">
              <span className="panel-label">{recordTitle}</span>
              <div className="score-board">
                <span className="score-line">
                  <span className="score-green">GREEN {record.player1}</span>
                  <span className="score-dash">–</span>
                  <span className="score-red">
                    {record.player2} {gameMode === 'ai' ? 'AI' : 'RED'}
                  </span>
                </span>
                {(record.player1 > 0 || record.player2 > 0) && (
                  <button
                    className="score-reset"
                    onClick={() => setRecord(resetRecord(gameMode, difficulty))}
                  >
                    Reset
                  </button>
                )}
              </div>

              <dl className="hud-readout">
                <div>
                  <dt>Mode</dt>
                  <dd>{MODES.find((m) => m.id === gameMode).label}</dd>
                </div>
                <div>
                  <dt>Setup</dt>
                  <dd>{setupLine}</dd>
                </div>
              </dl>

              <button
                className="start-btn"
                onClick={() => {
                  if (gameMode === 'online') {
                    // Online goes through the lobby (create/join) before starting.
                    setInLobby(true);
                    return;
                  }
                  // Unlock the audio context here: this click is the required
                  // user gesture, so all in-game playSound() calls stay audible.
                  initAudio();
                  audioUnlockedRef.current = true;
                  // Ranked is vs-AI only and opt-in, so an ordinary match still
                  // costs and pays nothing. The stake is taken here, before the
                  // first game, and beginSeries returns null if it cannot be
                  // covered — which leaves an ordinary unstaked match rather
                  // than starting a series nobody paid for.
                  const series = (ranked && gameMode === 'ai')
                    ? beginSeries(effectiveStake, difficulty)
                    : null;
                  gameStateRef.current = createGameState({
                    gameMode, difficulty, series,
                  });
                  setBalance(getBalance());
                  beginMatchIntro();
                  setGameStarted(true);
                }}
              >
                {gameMode === 'online' ? 'Play Online' : 'Start Game'}
              </button>
            </section>
          </main>

          {/* Reference, not instruction — nobody needs it to press Start, and
              on a phone it was the biggest block on the screen. Folded away
              there and left open on a desktop window that was showing it
              comfortably. It used to be display:none below a certain height,
              which is worse than folded: the information was simply gone. */}
          <footer className="hud-briefing">
            <Fold className="brief-fold" label="Field Briefing" defaultOpen={roomy}>
              <div className="brief-grid">
                {BRIEFING.map((item) => (
                  <div className="brief-item" key={item.title}>
                    <svg className="brief-icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path d={item.icon} />
                    </svg>
                    <span className="brief-text">
                      <span className="brief-title">{item.title}</span>
                      <span className="brief-body">{item.body}</span>
                    </span>
                  </div>
                ))}
              </div>
            </Fold>
            <div className="hud-footline" aria-hidden="true">
              <span>« Skills. Style. Altitude.</span>
              <span>Skydrop // Sector 07 »</span>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`game-stage${touchPrimary && portrait ? ' portrait' : ''}`}
      // Where the arena ends and the thumb console begins, upright. Handed to
      // the CSS as a property so stageBox stays the one statement of it.
      style={touchPrimary && portrait
        ? {
          '--band-top': `${stage.height}px`,
          '--band-h': `${Math.max(0, window.innerHeight - stage.height)}px`,
        }
        : undefined}
    >
      {/* Edge-to-edge backdrop. The arena is a fixed 4:3 and the screen is
          not, so rather than crop the play area (two-thirds of it, on a
          phone) or stretch the pixel art, the letterbox is filled with the
          same scene the arena is showing — blurred and dimmed so it reads as
          depth beyond the frame rather than as a second, wrong game. */}
      <div
        className="stage-bleed"
        style={{ backgroundImage: `url(${BLEED_SOURCE})` }}
      />
      <canvas
        ref={canvasRef}
        width={view.width}
        height={view.height}
        // Sized here rather than in CSS: the frame drawn into this canvas is
        // computed from exactly these numbers, and anything that sized the
        // element independently would stretch the art by the difference.
        style={{ width: `${stage.width}px`, height: `${stage.height}px` }}
        className={`game-canvas game-canvas-fullscreen${pointerSteering ? ' steering' : ''}`}
        onMouseMove={trackPointer}
        // Click to take the wheel. Deliberate, because a stray mouse movement
        // must never pull the rider away from someone playing on the keyboard.
        onMouseDown={(e) => {
          if (!hasFinePointer()) return;
          trackPointer(e);
          setSteering(true);
        }}
        // Right-click would otherwise open the browser menu over the arena.
        onContextMenu={(e) => e.preventDefault()}
      />
      {pointerSteering && (
        <div className="steer-chip">
          <span className="steer-dot" /> Mouse steering
          <span className="steer-hint">Arrow keys return to keyboard</span>
        </div>
      )}
      {/* Upright on a phone the header carries pause, controls and sound in
          its corners, so the button strip stands down there. */}
      {touchPrimary && portrait && !overlay && hud
        && !endState.winner && !endState.disconnected && (
        <Suspense fallback={null}>
          <MobileHud
            hud={hud}
            mySlot={localSlot(gameStateRef.current)}
            mode={gameStateRef.current.gameMode}
            muted={muted}
            onPause={togglePauseMenu}
            onControls={toggleControlsReference}
            onToggleSound={() => setMutedState((m) => !m)}
            onInset={reportHudInset}
          />
        </Suspense>
      )}
      {!overlay && !(touchPrimary && portrait) && !matchOver && (
        <div className={`hud-buttons${touchPrimary ? ' touch' : ''}`}>
          <button
            className="hud-btn"
            // Blur on click: left focused, the next Space or Enter — which are
            // player 2's kick and mode switch — would re-activate this button
            // instead of reaching the game.
            onClick={(e) => { e.currentTarget.blur(); toggleControlsReference(); }}
            title="Show controls (H)"
          >
            <kbd>H</kbd> Controls
          </button>
          <button
            className="hud-btn"
            onClick={(e) => { e.currentTarget.blur(); togglePauseMenu(); }}
            title="Pause menu — restart or quit (Esc)"
          >
            <kbd>Esc</kbd> Menu
          </button>
          <button
            className={`hud-btn hud-btn-icon${muted ? ' muted' : ''}`}
            onClick={(e) => { e.currentTarget.blur(); setMutedState((m) => !m); }}
            title={muted ? 'Unmute' : 'Mute'}
            aria-label={muted ? 'Unmute' : 'Mute'}
            aria-pressed={muted}
          >
            {muted ? '\u{1F507}' : '\u{1F50A}'}
          </button>
        </div>
      )}
      {/* A keyboard needs its keys shown; a touchscreen has its buttons. */}
      {!touchPrimary && !overlay && !matchOver && (
        <div className="key-hints" aria-hidden="true">
          {KEY_HINTS.map((hint) => (
            <span className="key-hint" key={hint.label}>
              {hint.keys.map((k) => <kbd key={k}>{k}</kbd>)}
              <span className="key-hint-label">{hint.label}</span>
            </span>
          ))}
        </div>
      )}
      {touchPrimary && !overlay && !matchOver && (
        <Suspense fallback={null}>
          <TouchControls
            keysRef={keysRef}
            keyPressedRef={keyPressedRef}
            padState={padState}
            // Upright, the console's size, so its frame is drawn true.
            frame={portrait
              ? { w: stage.width, h: Math.max(0, window.innerHeight - stage.height) }
              : undefined}
          />
        </Suspense>
      )}

      {/* The result, and the way on: rematch (or the next game of a series)
          and the menu, as buttons on every device — R and M still work. */}
      {matchOver && !overlay && endState.summary && (
        <EndScreen
          summary={endState.summary}
          touch={touchPrimary}
          band={touchPrimary && portrait}
          onRematch={() => {
            // Same cases as the R key, through the same helpers, so the two
            // cannot drift apart: carry the series on, re-stake, or rematch.
            const game = gameStateRef.current;
            if (seriesLive(game)) nextGameInSeries();
            else rematchSeries();
          }}
          onMenu={returnToMenu}
          onConsoleTop={reportEndConsoleTop}
        />
      )}

      {(overlay === 'reference' || overlay === 'intro') && (
        <Suspense fallback={null}>
          <ControlsScreen
            mode={gameStateRef.current.gameMode}
            playerSlot={onlineRef.current?.playerSlot}
            inMatch={overlay === 'reference'}
            onDismiss={overlay === 'reference' ? closeOverlay : dismissControls}
          />
        </Suspense>
      )}
      {overlay === 'pause' && (
        <PauseMenu
          mode={gameStateRef.current.gameMode}
          // The online guest re-syncs to host snapshots, so its own restart
          // would be overwritten within a frame.
          canRestart={
            gameStateRef.current.gameMode !== 'online'
            || onlineRef.current?.playerSlot === 'player1'
          }
          // Only counts while the series is still undecided — once it has been
          // settled there is nothing left to forfeit by leaving.
          stakeAtRisk={
            seriesLive(gameStateRef.current) ? gameStateRef.current.series.stake : 0
          }
          onResume={closeOverlay}
          onRestart={restartFromMenu}
          onQuit={returnToMenu}
        />
      )}
    </div>
  );
};

export default App;